import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './CountryRankings.css';
import { getCountryName } from './countryUtils';
import { countryPopulations } from './countryPopulations';
import CountryFlag from 'react-country-flag';

import { getQuarterlyIssues } from './quarterlyIssues';
import WorldRankingsMap from './WorldRankingsMap';
import CountryMatchups from './CountryMatchups';




function CountryRankings({ darkMode }) {
    const navigate = useNavigate();
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [countryFighters, setCountryFighters] = useState([]);
    const [loadingFighters, setLoadingFighters] = useState(false);
    // In-memory cache for fighter lists
    const fightersCache = React.useRef({});
    // Track if this is the initial mount for evolution chart defaults
    const isInitialMountEvolution = React.useRef(true);
    // Toggle: Gender (men or women)
    const [gender, setGender] = useState('men');
    // Toggle: Split Russia and North Caucasus (only for men)
    const [splitRussia, setSplitRussia] = useState(false);
    
    // Weight class names mapping
    const weightClassNames = {
        1: "Heavyweight",
        2: "L. Heavyweight",
        3: "Middleweight",
        4: "Welterweight",
        5: "Lightweight",
        6: "Featherweight",
        7: "Bantamweight",
        8: "Flyweight",
        13: "Strawweight",
        14: "Flyweight W",
        15: "Bantamweight W"
    };

    // Weight classes by gender
    const weightClassesByGender = {
        men: [1, 2, 3, 4, 5, 6, 7, 8],
        women: [13, 14, 15]
    };

    // Get all weight classes for current gender
    const allWeightClasses = weightClassesByGender[gender];

    // Weight class filter state - default to all weight classes for current gender
    const [selectedWeightClasses, setSelectedWeightClasses] = useState(weightClassesByGender[gender]);
    const [weightClassFilterOpen, setWeightClassFilterOpen] = useState(false);

    // Latest issue state - fetched from database
    const [latestIssue, setLatestIssue] = useState(null);
    
    // Calculate the number of quarters between two dates
    function getQuarterCount(startMonth, startYear, endMonth, endYear) {
        return ((startYear - endYear) * 4) + Math.round((startMonth - endMonth) / 3) + 1;
    }
    
    // Helper to calculate date for a given issue number (baseline: issue 144 = Oct 2025)
    function getDateForIssue(issueNum) {
        const baselineIssue = 144;
        const baselineDate = new Date(2025, 9, 1); // Oct 1, 2025
        const issuesDiff = issueNum - baselineIssue;
        const resultDate = new Date(baselineDate);
        resultDate.setMonth(resultDate.getMonth() + issuesDiff * 3);
        const month = String(resultDate.getMonth() + 1).padStart(2, '0');
        const day = String(resultDate.getDate()).padStart(2, '0');
        const year = resultDate.getFullYear();
        return `${month}/${day}/${year}`;
    }
    
    // Generate issue options based on latest issue
    const [issueOptions, setIssueOptions] = useState([]);
    const [issue, setIssue] = useState(null); // Will be set to latest issue after fetching
    // Gender-specific global rank defaults: men=1500, women=180
    const [globalRank, setGlobalRank] = useState(1000);
    const [rankings, setRankings] = useState([]);
    const [prevIssueRankings, setPrevIssueRankings] = useState([]);
    // Issue the rank-change arrows are measured against; '' = the issue right before the selected one
    const [compareIssue, setCompareIssue] = useState('');
    const [error, setError] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'fighters', direction: 'desc' });
    const [divisionRanks, setDivisionRanks] = useState([]);
    const [divisionError, setDivisionError] = useState('');

    // CountryEvolution states
    const [allCountriesEvolution, setAllCountriesEvolution] = useState([]);
    const [selectedCountriesEvolution, setSelectedCountriesEvolution] = useState([]);
    const [globalRankThresholdEvolution, setGlobalRankThresholdEvolution] = useState(1000);
    const [dataEvolution, setDataEvolution] = useState([]);
    const [loadingEvolution, setLoadingEvolution] = useState(false);
    const [errorEvolution, setErrorEvolution] = useState('');
    const [hoveredIssueEvolution, setHoveredIssueEvolution] = useState(null);
    const [hoveredCountryEvolution, setHoveredCountryEvolution] = useState(null);
    const [showRankingsEvolution, setShowRankingsEvolution] = useState(false);
    const [searchInputEvolution, setSearchInputEvolution] = useState('');
    const [showSuggestionsEvolution, setShowSuggestionsEvolution] = useState(false);
    const [cursorPosEvolution, setCursorPosEvolution] = useState({ x: 0, y: 0 });
    const [dateRangeStartEvolution, setDateRangeStartEvolution] = useState(null);
    const [dateRangeEndEvolution, setDateRangeEndEvolution] = useState(null);
    const [issueRangeMin, setIssueRangeMin] = useState(null);
    const [issueRangeMax, setIssueRangeMax] = useState(null);

    // ===== RankThreshold (fighters vs. global rank threshold) states =====
    const [selectedCountriesThreshold, setSelectedCountriesThreshold] = useState([]);
    const [issueThreshold, setIssueThreshold] = useState(null);
    const [dataThreshold, setDataThreshold] = useState({}); // { countryCode: sortedGlobalRanksArray }
    const [loadingThreshold, setLoadingThreshold] = useState(false);
    const [errorThreshold, setErrorThreshold] = useState('');
    const [searchInputThreshold, setSearchInputThreshold] = useState('');
    const [showSuggestionsThreshold, setShowSuggestionsThreshold] = useState(false);
    // Toggle: show country rank among all countries at each threshold, instead of fighter density (%)
    const [showRankThreshold, setShowRankThreshold] = useState(false);
    const [hoveredThresholdIdx, setHoveredThresholdIdx] = useState(null);
    const [hoveredCountryThreshold, setHoveredCountryThreshold] = useState(null);
    const [cursorPosThreshold, setCursorPosThreshold] = useState({ x: 0, y: 0 });
    const thresholdFightersCache = React.useRef({});

    // Helper function to get max global rank based on gender
    const getMaxGlobalRank = () => gender === 'women' ? 180 : 1500;


    // Fetch latest issue on mount
    React.useEffect(() => {
        const fetchLatestIssue = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_URL || '';
                const response = await fetch(`${apiBase}/api/latest-issue`);
                if (!response.ok) throw new Error('Failed to fetch latest issue');
                const data = await response.json();
                const latest = data.latest_issue;
                setLatestIssue(latest);
                setIssue(String(latest));
                setIssueThreshold(String(latest));
                
                // Generate issue options starting from the latest issue
                const latestDate = getDateForIssue(latest);
                // Calculate total quarters from latest issue back to Jan 1994
                const totalQuarters = getQuarterCount(
                    parseInt(latestDate.split('/')[0]),
                    parseInt(latestDate.split('/')[2]),
                    1,
                    1994
                );
                const options = getQuarterlyIssues(latest, latestDate, totalQuarters);
                setIssueOptions(options);
            } catch (err) {
                console.error('Error fetching latest issue:', err);
                // Fallback to 144 if fetch fails
                setLatestIssue(144);
                setIssue('144');
                setIssueThreshold('144');
                const fallbackOptions = getQuarterlyIssues(144, '10/01/2025', 128);
                setIssueOptions(fallbackOptions);
            }
        };
        
        fetchLatestIssue();
    }, []);

    // Fetch rankings on mount with default values
    React.useEffect(() => {
        if (issue !== null) {
            fetchRankings(issue, globalRank);
            fetchAvailableCountriesEvolution();
            
            // Set default countries for evolution chart and fetch their data
            const defaultCountries = ['US', 'BR', 'JP', 'UK', 'RU-WHOLE'];
            setSelectedCountriesEvolution(defaultCountries);
            // Fetch data for default countries immediately
            fetchEvolutionDataWithCountries(defaultCountries);
        }
    }, [issue]);

    // Set default countries for the rank-threshold chart and fetch their data on mount
    React.useEffect(() => {
        if (issueThreshold !== null) {
            const defaultCountriesThreshold = ['US', 'BR', 'JP', 'UK', 'RU-WHOLE'];
            setSelectedCountriesThreshold(defaultCountriesThreshold);
            fetchThresholdData(defaultCountriesThreshold, issueThreshold);
        }
    }, [issueThreshold]);

    // When gender changes, clear cache and selected country, then refetch rankings
    React.useEffect(() => {
        fightersCache.current = {};
        setSelectedCountry(null);
        setCountryFighters([]);
        // Reset weight classes to all for new gender
        const newWeightClasses = weightClassesByGender[gender];
        setSelectedWeightClasses(newWeightClasses);
        // Adjust global rank if it exceeds the gender-specific maximum
        const maxRank = gender === 'women' ? 180 : 1500;
        const newGlobalRank = globalRank > maxRank ? maxRank : globalRank;
        if (globalRank > maxRank) {
            setGlobalRank(newGlobalRank);
        }
        // Refetch rankings with new gender (using the adjusted values)
        if (issue && newGlobalRank) {
            fetchRankingsWithWeightClasses(issue, newGlobalRank, newWeightClasses);
        }
    }, [gender]);

    // When rankings mode changes in evolution chart, refetch data if we have selected countries
    React.useEffect(() => {
        if (selectedCountriesEvolution.length > 0 && dataEvolution.length > 0) {
            fetchEvolutionDataWithCountries(selectedCountriesEvolution);
        }
    }, [showRankingsEvolution]);

    // When rank mode changes in the threshold chart, refetch data (rank mode needs
    // every country's fighter counts, not just the selected ones, to compute standing)
    React.useEffect(() => {
        if (selectedCountriesThreshold.length > 0 && issueThreshold && Object.keys(dataThreshold).length > 0) {
            fetchThresholdData(selectedCountriesThreshold, issueThreshold);
        }
    }, [showRankThreshold]);

    // Auto-fetch data when countries are selected
    React.useEffect(() => {
        if (selectedCountriesEvolution.length > 0) {
            // On initial mount, skip the fetch here since it will be handled by the mount effect
            if (isInitialMountEvolution.current) {
                isInitialMountEvolution.current = false;
                return;
            }
            fetchEvolutionDataWithCountries(selectedCountriesEvolution);
        }
    }, [selectedCountriesEvolution]);

    // Calculate the number of quarters between Oct 2025 and Jan 2000
    function getQuarterCount(startMonth, startYear, endMonth, endYear) {
        return ((startYear - endYear) * 4) + Math.round((startMonth - endMonth) / 3) + 1;
    }

    const fetchRankings = async (curIssue, curGlobalRank) => {
        fetchRankingsWithWeightClasses(curIssue, curGlobalRank, selectedWeightClasses);
    };

    const fetchRankingsWithWeightClasses = async (curIssue, curGlobalRank, weightClasses) => {
        setError('');
        setRankings([]);
        setDivisionRanks([]);
        setDivisionError('');
        const apiBase = process.env.REACT_APP_API_URL || '';
        if (!curIssue || !curGlobalRank) {
            setError('Please enter both values.');
            return;
        }
        try {
            // Build weight classes parameter
            const weightClassesParam = weightClasses.length > 0 ? `&weight_classes=${weightClasses.join(',')}` : '';
            const url = `${apiBase}/api/country-rankings?issue=${curIssue}&global_rank=${curGlobalRank}&gender=${gender}${weightClassesParam}`;
            // Fetch country rankings with gender parameter
            const res = await fetch(url);
            const data = await res.json();
            if (res.ok) {
                setRankings(data);
            } else {
                setError(data.error || 'Error fetching data');
            }
        } catch (e) {
            setError('Server error');
        }
        try {
            // Build weight classes parameter
            const weightClassesParam = weightClasses.length > 0 ? `&weight_classes=${weightClasses.join(',')}` : '';
            // Fetch division max ranks with gender parameter
            const divRes = await fetch(
                `${apiBase}/api/division-max-rank?issue=${curIssue}&global_rank=${curGlobalRank}&gender=${gender}${weightClassesParam}`
            );
            const divData = await divRes.json();
            if (divRes.ok) {
                setDivisionRanks(divData);
                setDivisionError('');
            } else {
                setDivisionError(divData.error || 'Error fetching division data');
            }
        } catch (e) {
            console.error('Division fetch error:', e);
            setDivisionError('Server error');
        }
        fetchPreviousIssueRankings(curIssue, curGlobalRank, weightClasses);
    };

    // Fetch the baseline issue's rankings (same filters) purely to compute rank
    // gained/lost per country. The baseline is the user-chosen compareTo issue if
    // it's earlier than the current one, otherwise the prior quarter (applied on
    // "Apply" like the other filters). Silently
    // clears on failure or if there's no earlier issue to compare against.
    const fetchPreviousIssueRankings = async (curIssue, curGlobalRank, weightClasses) => {
        const prevIssueNum = compareIssue && Number(compareIssue) < Number(curIssue)
            ? Number(compareIssue)
            : Number(curIssue) - 1;
        if (!issueOptions.some(o => Number(o.issue) === prevIssueNum)) {
            setPrevIssueRankings([]);
            return;
        }
        try {
            const apiBase = process.env.REACT_APP_API_URL || '';
            const weightClassesParam = weightClasses.length > 0 ? `&weight_classes=${weightClasses.join(',')}` : '';
            const url = `${apiBase}/api/country-rankings?issue=${prevIssueNum}&global_rank=${curGlobalRank}&gender=${gender}${weightClassesParam}`;
            const res = await fetch(url);
            const data = await res.json();
            setPrevIssueRankings(res.ok && Array.isArray(data) ? data : []);
        } catch (e) {
            setPrevIssueRankings([]);
        }
    };

    // Sorting logic
    const getSortIcon = (key) => {
        const activeColor = '#888';
        const inactiveColor = '#bdbdbd';
        const isActive = sortConfig.key === key;
        const direction = isActive ? sortConfig.direction : null;
        
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: '0.75em', color: direction === 'asc' ? activeColor : direction === 'desc' ? activeColor : inactiveColor }}>
                {direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↑↓'}
            </span>
        );
    };

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };


    // Prepare sorted data, and filter Russia/North Caucasus based on toggle (only for men)
    const sortedRankings = React.useMemo(() => {

        if (!rankings || rankings.length === 0) return rankings;

        // For women: return rankings as-is, no synthetic rows or splitting
        if (gender === 'women') {
            const arr = sortConfig.key
                ? [...rankings].sort((a, b) => {
                    let aValue, bValue;
                    switch (sortConfig.key) {
                        case 'country':
                            aValue = getCountryDisplayName(a.country_code === 'UK' ? 'GB' : a.country_code).toLowerCase();
                            bValue = getCountryDisplayName(b.country_code === 'UK' ? 'GB' : b.country_code).toLowerCase();
                            break;
                        case 'fighters':
                            aValue = a.fighter_count;
                            bValue = b.fighter_count;
                            break;
                        case 'perMillion': {
                            const popA = countryPopulations[a.country_code === 'UK' ? 'GB' : a.country_code];
                            const popB = countryPopulations[b.country_code === 'UK' ? 'GB' : b.country_code];
                            aValue = (a.fighter_count < 2) ? Infinity : (popA && popA > 0 ? a.fighter_count / (popA / 1_000_000) : -Infinity);
                            bValue = (b.fighter_count < 2) ? Infinity : (popB && popB > 0 ? b.fighter_count / (popB / 1_000_000) : -Infinity);
                            break;
                        }
                        default:
                            return 0;
                    }
                    if (aValue === Infinity && bValue === Infinity) return 0;
                    if (aValue === Infinity) return 1;
                    if (bValue === Infinity) return -1;
                    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                })
                : rankings;
            return arr;
        }

        // For men: apply Russia/North Caucasus and Europe logic

        // Codes for Russia and North Caucasus
        const RU_CODES = [
            'RU', 'RU-CE', 'RU-DA', 'RU-IN', 'RU-SE', 'RU-KB', 'RU-KC'
        ];
        // List of all European country codes (ISO 3166-1 alpha-2)
        const EUROPE_CODES = [
            'AL','AD','AT','BY','BE','BA','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IS','IE','IT','LV','LI','LT','LU','MT','MD','MC','ME','NL','MK','NO','PL','PT','RO','SM','RS','SK','SI','ES','SE','CH','UA','GB','UK','VA'
        ];

        // Find all Russia and North Caucasus rows
        let ruWholeFighterCount = 0;
        let foundAny = false;
        rankings.forEach(row => {
            if (RU_CODES.includes(row.country_code)) {
                ruWholeFighterCount += row.fighter_count;
                foundAny = true;
            }
        });

        // Compute Europe (excluding Russia) row
        let europeFighterCount = 0;
        let europePop = 0;
        rankings.forEach(row => {
            if (EUROPE_CODES.includes(row.country_code)) {
                europeFighterCount += row.fighter_count;
                const pop = countryPopulations[row.country_code];
                if (pop) europePop += pop;
            }
        });

        let filteredRankings;
        if (splitRussia) {
            // Show Russia (without North Caucasus) and each North Caucasus republic as separate rows, hide RU-WHOLE
            filteredRankings = rankings.filter(row => {
                // Show all except RU-WHOLE
                if (row.country_code === 'RU-WHOLE') return false;
                // Show RU and all North Caucasus
                if (RU_CODES.includes(row.country_code)) return true;
                // Show all other countries
                return !RU_CODES.includes(row.country_code) && row.country_code !== 'RU-WHOLE';
            });
        } else {
            // Show only RU-WHOLE (if any Russia-related row exists), hide RU and North Caucasus
            // Also exclude North Caucasus republics from main rankings
            const NC_CODES = ['RU-CE', 'RU-DA', 'RU-IN', 'RU-SE', 'RU-KB', 'RU-KC'];
            filteredRankings = rankings.filter(row => {
                // Exclude North Caucasus republics from main rankings
                if (NC_CODES.includes(row.country_code)) return false;
                if (row.country_code === 'RU-WHOLE') return true;
                // Hide RU 
                if (row.country_code === 'RU') return false;
                // Show all other countries
                return true;
            });
            // If RU-WHOLE is not present but Russia-related rows exist, add synthetic RU-WHOLE row
            if (!filteredRankings.find(r => r.country_code === 'RU-WHOLE') && foundAny) {
                filteredRankings = [
                    ...filteredRankings,
                    {
                        country_code: 'RU-WHOLE',
                        fighter_count: ruWholeFighterCount
                    }
                ];
            }
        }

        // Add Europe (excluding Russia) row if there are any European fighters
        if (europeFighterCount > 0) {
            // Avoid duplicate if already present
            if (!filteredRankings.find(r => r.country_code === 'EU-NORU')) {
                filteredRankings = [
                    ...filteredRankings,
                    {
                        country_code: 'EU-NORU',
                        fighter_count: europeFighterCount,
                        _europePop: europePop // for per million calculation
                    }
                ];
            }
        }

        const codeForPop = (row) => row.country_code === 'UK' ? 'GB' : row.country_code;
        const arr = sortConfig.key
            ? [...filteredRankings].sort((a, b) => {
                let aValue, bValue;
                switch (sortConfig.key) {
                    case 'country':
                        aValue = getCountryDisplayName(codeForPop(a)).toLowerCase();
                        bValue = getCountryDisplayName(codeForPop(b)).toLowerCase();
                        break;
                    case 'fighters':
                        aValue = a.fighter_count;
                        bValue = b.fighter_count;
                        break;
                    case 'perMillion': {
                        let popA, popB;
                        if (a.country_code === 'EU-NORU') {
                            popA = a._europePop;
                        } else {
                            popA = countryPopulations[codeForPop(a)];
                        }
                        if (b.country_code === 'EU-NORU') {
                            popB = b._europePop;
                        } else {
                            popB = countryPopulations[codeForPop(b)];
                        }
                        aValue = (a.fighter_count < 2) ? Infinity : (popA && popA > 0 ? a.fighter_count / (popA / 1_000_000) : -Infinity);
                        bValue = (b.fighter_count < 2) ? Infinity : (popB && popB > 0 ? b.fighter_count / (popB / 1_000_000) : -Infinity);
                        break;
                    }
                    default:
                        return 0;
                }
                // Always push Infinity (countries with <2 fighters) to the bottom
                if (aValue === Infinity && bValue === Infinity) return 0;
                if (aValue === Infinity) return 1;
                if (bValue === Infinity) return -1;
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            })
            : filteredRankings;
        return arr;
    }, [rankings, sortConfig, splitRussia, gender]);

    // Helper: is North Caucasus republic code
    const isNorthCaucasus = code => [
        'RU-CE', 'RU-DA', 'RU-IN', 'RU-SE', 'RU-KB', 'RU-KC'
    ].includes(code);

    // Helper: get country name with gender-specific adjustments
    const getCountryDisplayName = (code) => {
        let name = getCountryName(code);
        // For women's rankings, Russia should just be "Russia"
        if (gender === 'women' && code === 'RU') {
            name = 'Russia';
        }
        return name;
    };

    // Build country_code -> rank and country_code -> fighter count maps from a
    // raw rankings array. Ranks are by fighter count or per-million (`metric`),
    // always highest first and independent of the table's sort direction,
    // applying the same Russia/North Caucasus aggregation as sortedRankings so
    // ranks line up with what's actually shown as rows. Used to compare the
    // current issue's ranks against the baseline issue's for the +/- indicator.
    const buildRankMap = (rawRows, genderVal, splitRussiaVal, metric = 'fighters') => {
        const rankMap = new Map();
        const countMap = new Map();
        if (!rawRows || rawRows.length === 0) return { rankMap, countMap };

        let aggregated;
        if (genderVal === 'women') {
            aggregated = rawRows;
        } else {
            const RU_CODES = ['RU', 'RU-CE', 'RU-DA', 'RU-IN', 'RU-SE', 'RU-KB', 'RU-KC'];
            const NC_CODES = ['RU-CE', 'RU-DA', 'RU-IN', 'RU-SE', 'RU-KB', 'RU-KC'];
            let ruWholeFighterCount = 0;
            let foundAny = false;
            rawRows.forEach(row => {
                if (RU_CODES.includes(row.country_code)) {
                    ruWholeFighterCount += row.fighter_count;
                    foundAny = true;
                }
            });
            if (splitRussiaVal) {
                aggregated = rawRows.filter(row => row.country_code !== 'RU-WHOLE');
            } else {
                aggregated = rawRows.filter(row => !NC_CODES.includes(row.country_code) && row.country_code !== 'RU');
                if (!aggregated.find(r => r.country_code === 'RU-WHOLE') && foundAny) {
                    aggregated = [...aggregated, { country_code: 'RU-WHOLE', fighter_count: ruWholeFighterCount }];
                }
            }
        }
        // EU-NORU is a synthetic aggregate, not a real country - it never
        // occupies a rank slot (sortedRankings/getRankNumber always exclude it
        // from counting), so it's left out of the rank map entirely.

        aggregated.forEach(row => countMap.set(row.country_code, row.fighter_count));

        // Value each country is ranked on. Per-million mirrors the table: it needs
        // >= 2 fighters and a known population, otherwise the country is unranked.
        const metricValue = (row) => {
            if (metric !== 'perMillion') return row.fighter_count;
            const pop = countryPopulations[row.country_code === 'UK' ? 'GB' : row.country_code];
            return row.fighter_count >= 2 && pop > 0 ? row.fighter_count / (pop / 1_000_000) : null;
        };
        const sorted = aggregated
            .map(row => ({ code: row.country_code, value: metricValue(row) }))
            .filter(r => r.value !== null)
            .sort((a, b) => b.value - a.value);
        sorted.forEach((r, idx) => {
            if (idx > 0 && sorted[idx - 1].value === r.value) {
                rankMap.set(r.code, rankMap.get(sorted[idx - 1].code));
            } else {
                rankMap.set(r.code, idx + 1);
            }
        });
        return { rankMap, countMap };
    };

    // Rank changes follow whichever column the table is sorted by: per-million
    // when sorting by that column, fighter count otherwise.
    const rankMetric = sortConfig.key === 'perMillion' ? 'perMillion' : 'fighters';
    const { rankMap: currentRankMap } = React.useMemo(
        () => buildRankMap(rankings, gender, splitRussia, rankMetric),
        [rankings, gender, splitRussia, rankMetric]
    );
    const { rankMap: prevRankMap, countMap: prevCountMap } = React.useMemo(
        () => buildRankMap(prevIssueRankings, gender, splitRussia, rankMetric),
        [prevIssueRankings, gender, splitRussia, rankMetric]
    );

    // Helper: rank-change indicator (+/- spots since the previous issue) for a row
    const getRankChange = (row) => {
        if (row.country_code === 'EU-NORU') return null;
        const curRank = currentRankMap.get(row.country_code);
        if (curRank == null || prevCountMap.size === 0) return null; // unranked now, or no baseline loaded
        const prevRank = prevRankMap.get(row.country_code);
        // Not ranked in the baseline (no fighters then, or too few for per-million) = new to the ranking
        if (prevRank == null) return 'new';
        return prevRank - curRank; // positive = moved up (gained spots)
    };

    // Helper: fighter-count change vs. the baseline issue for a row (a country
    // absent from the baseline had 0 fighters then); null if there's no baseline
    const getCountChange = (row) => {
        if (row.country_code === 'EU-NORU' || prevCountMap.size === 0) return null;
        return row.fighter_count - (prevCountMap.get(row.country_code) ?? 0);
    };

    // Helper: get the actual rank number for a row, accounting for ties
    const getRankNumber = (row, idx) => {
        if (row.country_code === 'EU-NORU') return '';
        
        // Count non-EU-NORU rows before this one
        const visibleRowsBefore = sortedRankings.slice(0, idx).filter(r => r.country_code !== 'EU-NORU').length;
        const baseRank = visibleRowsBefore + 1;
        
        // Check if this row is tied with the previous row (same fighter count)
        if (idx > 0) {
            const prevRow = sortedRankings[idx - 1];
            if (prevRow.fighter_count === row.fighter_count) {
                // This row is tied with the previous row, show nothing
                return '=';
            }
        }
        
        // This is the first row of its rank (no tie with previous)
        return baseRank;
    };

    // ===== CountryEvolution Helper Functions =====
    
    // Fetch available countries for evolution chart
    const fetchAvailableCountriesEvolution = async () => {
        try {
            const apiBase = process.env.REACT_APP_API_URL || '';
            const res = await fetch(`${apiBase}/api/country-rankings?issue=144&global_rank=1500`);
            const data = await res.json();
            if (Array.isArray(data)) {
                // Get countries from API
                let countries = data.map(row => row.country_code).sort();
                
                // North Caucasus republic codes to filter out
                const NC_CODES = ['RU-CE', 'RU-DA', 'RU-IN', 'RU-SE', 'RU-KB', 'RU-KC'];
                
                // Filter out 'RU' and North Caucasus republics
                countries = countries.filter(c => c !== 'RU' && !NC_CODES.includes(c));
                
                // Ensure common countries are included (in case API doesn't return them)
                const commonCountries = ['US', 'BR', 'UK', 'CA', 'AU', 'MX', 'JP', 'DE', 'FR'];
                countries = Array.from(new Set([...countries, ...commonCountries])).sort();
                
                // Remove 'RU' and North Caucasus from final list since we use 'RU-WHOLE' for aggregation
                countries = countries.filter(c => c !== 'RU' && !NC_CODES.includes(c));
                
                // Add synthetic aggregation options for Russia
                countries = [...countries, 'RU-WHOLE'];
                
                countries.sort();
                setAllCountriesEvolution(countries);
            }
        } catch (e) {
            console.error('Error fetching available countries:', e);
        }
    };

    // Generate colors for countries in evolution chart
    const getCountryColorEvolution = (countryCode) => {
        const colors = [
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
            '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B88B', '#ABEBC6',
            '#F1948A', '#AED6F1', '#F5B041', '#D7BDE2', '#A9DFBF'
        ];
        const index = selectedCountriesEvolution.indexOf(countryCode) % colors.length;
        return colors[index];
    };

    // Process data for evolution chart
    const chartDataEvolution = useMemo(() => {
        if (!dataEvolution || dataEvolution.length === 0) {
            return { issues: [], issueMap: {}, issueToDateMap: {} };
        }

        // Get all unique issues from the data (backend only returns issues where countries have fighters)
        const issuesSet = new Set();
        dataEvolution.forEach(row => issuesSet.add(row.issue));
        let issuesFromData = Array.from(issuesSet).sort((a, b) => a - b);

        // Filter by date range if specified
        if (issueRangeMin !== null || issueRangeMax !== null) {
            const startIssue = issueRangeMin !== null ? issueRangeMin : Math.min(...issuesFromData);
            const endIssue = issueRangeMax !== null ? issueRangeMax : Math.max(...issuesFromData);
            issuesFromData = issuesFromData.filter(issue => issue >= startIssue && issue <= endIssue);
        }

        // Get the latest issue to generate date mappings
        const latestIssue = Math.max(...issuesFromData);
        // Calculate the correct date for the latest issue based on the baseline (issue 144 = Oct 2025)
        const baselineIssue = 144;
        const baselineDate = new Date(2025, 9, 1); // Oct 1, 2025
        const issuesDiff = latestIssue - baselineIssue;
        const correctLatestDate = new Date(baselineDate);
        correctLatestDate.setMonth(correctLatestDate.getMonth() + issuesDiff * 3);
        const latestDateStr = `${String(correctLatestDate.getMonth() + 1).padStart(2, '0')}/${String(correctLatestDate.getDate()).padStart(2, '0')}/${correctLatestDate.getFullYear()}`;
        const allQuarterlyIssues = getQuarterlyIssues(latestIssue, latestDateStr, 200);
        const issueToDateMap = {};
        allQuarterlyIssues.forEach(item => {
            issueToDateMap[item.issue] = item.date;
        });

        // Get min issue from data to know the full range
        const minIssue = Math.min(...issuesFromData);
        
        // Build complete issues list: all issues from min to max (quarterly issues)
        // getQuarterlyIssues returns issues in descending order, so reverse to get ascending order
        const issuesEvol = allQuarterlyIssues
            .filter(item => item.issue >= minIssue && item.issue <= latestIssue)
            .reverse()
            .map(item => item.issue);

        // Create map: issue -> country -> fighter_proportion (as percentage)
        // Use total_fighter_count from backend for accurate proportions
        const issueMap = {};
        issuesEvol.forEach(issue => {
            issueMap[issue] = {};
        });

        dataEvolution.forEach(row => {
            if (issueMap[row.issue]) {
                const total = row.total_fighter_count || 1; // fallback to 1 to avoid division by zero
                const proportion = total > 0 ? (row.fighter_count / total) * 100 : 0;
                issueMap[row.issue][row.country_code] = proportion;
            }
        });

        // If in rankings mode, convert proportions to rankings
        if (showRankingsEvolution) {
            issuesEvol.forEach(issue => {
                // Get ALL countries with their fighter counts for this issue (not just selected)
                const countryValues = [];
                
                // Collect data for all countries that have data for this issue
                dataEvolution.forEach(row => {
                    if (row.issue === issue) {
                        const existingEntry = countryValues.find(cv => cv.country === row.country_code);
                        if (existingEntry) {
                            existingEntry.count = row.fighter_count;
                        } else {
                            countryValues.push({ country: row.country_code, count: row.fighter_count });
                        }
                    }
                });
                
                // Sort by count descending to get rankings
                countryValues.sort((a, b) => b.count - a.count);
                
                // Assign rankings (1-based, with ties getting same rank)
                const rankings = {};
                let currentRank = 1;
                for (let i = 0; i < countryValues.length; i++) {
                    const country = countryValues[i].country;
                    
                    // If this is the first country, or count is different from previous, assign new rank
                    if (i === 0 || countryValues[i].count !== countryValues[i - 1].count) {
                        currentRank = i + 1;
                    }
                    rankings[country] = currentRank;
                }
                
                // Replace proportions with rankings in issueMap
                for (const country of selectedCountriesEvolution) {
                    issueMap[issue][country] = rankings[country] || countryValues.length + 1;
                }
            });
        }

        // Fill in missing issues with 0 (or 999999 for rankings) for all selected countries
        selectedCountriesEvolution.forEach(country => {
            issuesEvol.forEach(issue => {
                if (!(issue in issueMap)) {
                    issueMap[issue] = {};
                }
                if (!(country in issueMap[issue])) {
                    issueMap[issue][country] = showRankingsEvolution ? 999999 : 0;
                }
            });
        });

        return { issues: issuesEvol, issueMap, issueToDateMap };
    }, [dataEvolution, selectedCountriesEvolution, showRankingsEvolution, issueRangeMin, issueRangeMax]);

    // Get filtered countries for evolution search
    const getFilteredCountriesEvolution = () => {
        if (!searchInputEvolution.trim()) return [];
        const input = searchInputEvolution.toLowerCase();
        
        // Filter countries based on both name and code match
        const filtered = allCountriesEvolution.filter(country => {
            const name = getCountryName(country).toLowerCase();
            const code = country.toLowerCase();
            
            // Match if search term appears in either name or code
            return name.includes(input) || code.includes(input);
        });
        
        // Return all matches (no limit)
        return filtered;
    };

    // Add country to evolution chart
    const addCountryEvolution = (countryCode) => {
        if (!selectedCountriesEvolution.includes(countryCode)) {
            setSelectedCountriesEvolution(prev => [...prev, countryCode]);
            setSearchInputEvolution('');
            setShowSuggestionsEvolution(false);
            
            // Fetch data immediately with the updated countries list
            const updatedCountries = [...selectedCountriesEvolution, countryCode];
            fetchEvolutionDataWithCountries(updatedCountries);
        } else {
            setSearchInputEvolution('');
            setShowSuggestionsEvolution(false);
        }
    };

    // Fetch evolution data for specific countries
    const fetchEvolutionDataWithCountries = async (countries) => {
        if (countries.length === 0) {
            setErrorEvolution('Please select at least one country');
            return;
        }

        setLoadingEvolution(true);
        setErrorEvolution('');
        try {
            const apiBase = process.env.REACT_APP_API_URL || '';
            
            // When in rankings mode, we need ALL countries' data to properly rank
            // Otherwise, just fetch the selected countries
            let countryStr;
            if (showRankingsEvolution) {
                // Fetch all available countries for global rankings
                countryStr = allCountriesEvolution.join(',');
            } else {
                // Fetch only selected countries for proportion mode
                countryStr = countries.join(',');
            }
            
            const res = await fetch(
                `${apiBase}/api/country-fighters-evolution?countries=${countryStr}&global_rank_threshold=${globalRankThresholdEvolution}`
            );
            const result = await res.json();
            if (Array.isArray(result)) {
                setDataEvolution(result);
            } else {
                setErrorEvolution(result.error || 'Failed to fetch data');
            }
        } catch (e) {
            setErrorEvolution('Server error: ' + e.message);
        } finally {
            setLoadingEvolution(false);
        }
    };

    // Fetch evolution data
    const fetchEvolutionData = async () => {
        fetchEvolutionDataWithCountries(selectedCountriesEvolution);
    };

    // Remove country from evolution
    const removeCountryEvolution = (countryCode) => {
        setSelectedCountriesEvolution(prev => prev.filter(c => c !== countryCode));
    };

    // Get max proportion for evolution chart scaling
    const getMaxProportionEvolution = () => {
        // Find the maximum proportion value in the data
        if (!chartDataEvolution.issues || chartDataEvolution.issues.length === 0) return showRankingsEvolution ? selectedCountriesEvolution.length : 100;
        
        let maxProportion = 0;
        selectedCountriesEvolution.forEach(country => {
            chartDataEvolution.issues.forEach(issue => {
                const proportion = chartDataEvolution.issueMap[issue]?.[country] || 0;
                if (proportion > maxProportion) {
                    maxProportion = proportion;
                }
            });
        });
        
        if (showRankingsEvolution) {
            // For rankings, just return the max rank (or number of selected countries)
            return Math.ceil(maxProportion) || selectedCountriesEvolution.length;
        } else {
            // Add 10% padding above the max and round up to nearest 5%
            const paddedMax = maxProportion * 1.1;
            return Math.ceil(paddedMax / 5) * 5;
        }
    };

    // Get Y coordinate for evolution chart
    const getYEvolution = (proportion) => {
        const max = getMaxProportionEvolution();
        const widthEvol = 900;
        const heightEvol = 520;
        const paddingEvol = { top: 40, right: 180, bottom: 60, left: 60 };
        const chartHeightEvol = heightEvol - paddingEvol.top - paddingEvol.bottom;
        
        if (showRankingsEvolution) {
            // For rankings: lower rank number (1 = best) should be at TOP
            // So we invert: top of chart is rank 1, bottom is max rank
            return paddingEvol.top + (proportion / max) * chartHeightEvol;
        } else {
            // For percentages: higher values go up
            return paddingEvol.top + chartHeightEvol - (proportion / max) * chartHeightEvol;
        }
    };

    // Get X coordinate for evolution chart
    const getXEvolution = (issueIndex) => {
        const widthEvol = 900;
        const paddingEvol = { top: 40, right: 180, bottom: 60, left: 60 };
        const chartWidthEvol = widthEvol - paddingEvol.left - paddingEvol.right;

        if (chartDataEvolution.issues.length === 0) return 0;
        return paddingEvol.left + (issueIndex / (chartDataEvolution.issues.length - 1 || 1)) * chartWidthEvol;
    };

    // ===== RankThreshold Helper Functions =====

    // Fetch the raw fighter list for a single (non-aggregate) country code
    const fetchFightersForCodeThreshold = async (code, issueVal, genderVal, maxRank) => {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${apiBase}/api/country-fighters?issue=${issueVal}&global_rank=${maxRank}&country_code=${code}&gender=${genderVal}`);
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : [];
    };

    // Get sorted global ranks for a country (handles RU-WHOLE/EU-NORU aggregates), cached per issue+gender+code
    const fetchRanksForCountryThreshold = async (code, issueVal, genderVal) => {
        const maxRank = genderVal === 'women' ? 180 : 1500;
        const cacheKey = `${issueVal}_${genderVal}_${code}`;
        if (thresholdFightersCache.current[cacheKey]) {
            return thresholdFightersCache.current[cacheKey];
        }
        let fighters;
        if (code === 'RU-WHOLE') {
            const RU_CODES = ['RU', 'RU-CE', 'RU-DA', 'RU-IN', 'RU-SE', 'RU-KB', 'RU-KC'];
            const lists = await Promise.all(RU_CODES.map(c => fetchFightersForCodeThreshold(c, issueVal, genderVal, maxRank)));
            fighters = lists.flat();
        } else if (code === 'EU-NORU') {
            const EUROPE_CODES = [
                'AL','AD','AT','BY','BE','BA','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IS','IE','IT','LV','LI','LT','LU','MT','MD','MC','ME','NL','MK','NO','PL','PT','RO','SM','RS','SK','SI','ES','SE','CH','UA','GB','UK','VA'
            ];
            const lists = await Promise.all(EUROPE_CODES.map(c => fetchFightersForCodeThreshold(c, issueVal, genderVal, maxRank)));
            fighters = lists.flat();
        } else {
            fighters = await fetchFightersForCodeThreshold(code, issueVal, genderVal, maxRank);
        }
        const ranks = fighters
            .map(f => f.global_rank)
            .filter(r => r != null)
            .sort((a, b) => a - b);
        thresholdFightersCache.current[cacheKey] = ranks;
        return ranks;
    };

    // Fetch and store rank data for a set of countries at the selected issue
    const fetchThresholdData = async (countries, issueVal) => {
        if (!countries || countries.length === 0 || !issueVal) {
            setDataThreshold({});
            return;
        }
        setLoadingThreshold(true);
        setErrorThreshold('');
        try {
            // Rank mode needs every country's counts to determine each selected
            // country's relative standing, not just the ones the user picked.
            const countriesToFetch = showRankThreshold
                ? Array.from(new Set([...allCountriesEvolution, ...countries]))
                : countries;
            const entries = await Promise.all(countriesToFetch.map(async code => {
                const ranks = await fetchRanksForCountryThreshold(code, issueVal, gender);
                return [code, ranks];
            }));
            setDataThreshold(Object.fromEntries(entries));
        } catch (e) {
            setErrorThreshold('Server error: ' + e.message);
        } finally {
            setLoadingThreshold(false);
        }
    };

    // Add country to the rank-threshold chart
    const addCountryThreshold = (countryCode) => {
        if (!selectedCountriesThreshold.includes(countryCode)) {
            const updated = [...selectedCountriesThreshold, countryCode];
            setSelectedCountriesThreshold(updated);
            setSearchInputThreshold('');
            setShowSuggestionsThreshold(false);
            if (issueThreshold) {
                fetchThresholdData(updated, issueThreshold);
            }
        } else {
            setSearchInputThreshold('');
            setShowSuggestionsThreshold(false);
        }
    };

    // Remove country from the rank-threshold chart
    const removeCountryThreshold = (countryCode) => {
        setSelectedCountriesThreshold(prev => prev.filter(c => c !== countryCode));
        setDataThreshold(prev => {
            const updated = { ...prev };
            delete updated[countryCode];
            return updated;
        });
    };

    // Get filtered countries for the rank-threshold search box
    const getFilteredCountriesThreshold = () => {
        if (!searchInputThreshold.trim()) return [];
        const input = searchInputThreshold.toLowerCase();
        return allCountriesEvolution.filter(country => {
            const name = getCountryName(country).toLowerCase();
            const code = country.toLowerCase();
            return name.includes(input) || code.includes(input);
        });
    };

    // Generate colors for countries in the rank-threshold chart
    const getCountryColorThreshold = (countryCode) => {
        const colors = [
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
            '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B88B', '#ABEBC6',
            '#F1948A', '#AED6F1', '#F5B041', '#D7BDE2', '#A9DFBF'
        ];
        const index = selectedCountriesThreshold.indexOf(countryCode) % colors.length;
        return colors[index];
    };

    // Global rank thresholds shown on the X-axis: steps of 10 up to 100, then steps of 100
    // beyond that (up to the gender-specific max), to keep the point count - and the
    // per-threshold computation in chartDataThreshold - manageable.
    const thresholdsListThreshold = useMemo(() => {
        const maxRank = gender === 'women' ? 180 : 1500;
        const arr = [];
        for (let t = 10; t <= Math.min(100, maxRank); t += 10) arr.push(t);
        for (let t = 200; t <= maxRank; t += 100) arr.push(t);
        if (arr[arr.length - 1] !== maxRank) arr.push(maxRank);
        return arr;
    }, [gender]);

    // Fixed, round tick values for the X-axis
    const thresholdTickValues = useMemo(() => {
        const maxRank = gender === 'women' ? 180 : 1500;
        // Top 10 is left out: a single fighter there is worth 10%, which dominates the radar
        const candidates = [20, 50, 100, 200, 500, 1000, 1500];
        const ticks = candidates.filter(v => v <= maxRank);
        if (ticks[ticks.length - 1] !== maxRank) ticks.push(maxRank);
        return ticks;
    }, [gender]);

    // Fighters-per-threshold, as a percentage of the threshold itself (density of that country within top N),
    // or, in rank mode, the country's rank by fighter count among every country at that same threshold.
    const chartDataThreshold = useMemo(() => {
        const result = {};

        if (!showRankThreshold) {
            selectedCountriesThreshold.forEach(code => {
                const ranks = dataThreshold[code] || [];
                const percents = [];
                let idx = 0;
                thresholdsListThreshold.forEach(t => {
                    while (idx < ranks.length && ranks[idx] <= t) idx++;
                    percents.push((idx / t) * 100);
                });
                result[code] = percents;
            });
            return result;
        }

        // Rank mode: at each threshold, count fighters within top-t for every country
        // we have data for, then rank the selected countries by that count (ties share a rank).
        const allCodes = Object.keys(dataThreshold);
        const cursors = {};
        allCodes.forEach(code => { cursors[code] = 0; });
        selectedCountriesThreshold.forEach(code => { result[code] = []; });

        thresholdsListThreshold.forEach((t, tIdx) => {
            const counts = allCodes.map(code => {
                const ranks = dataThreshold[code] || [];
                let idx = cursors[code];
                while (idx < ranks.length && ranks[idx] <= t) idx++;
                cursors[code] = idx;
                return { code, count: idx };
            }).sort((a, b) => b.count - a.count);

            let rank = 1;
            counts.forEach((c, i) => {
                if (!(i > 0 && counts[i - 1].count === c.count)) {
                    rank = i + 1;
                }
                if (result[c.code]) {
                    result[c.code][tIdx] = rank;
                }
            });
        });

        return result;
    }, [dataThreshold, selectedCountriesThreshold, thresholdsListThreshold, showRankThreshold]);

    // Radar chart: one axis (vertex) per x-axis tick (10, 20, 50, 100, ...), laid out clockwise from the top.
    const radarTicks = thresholdTickValues;
    const thresholdChartWidth = 640;
    const thresholdChartHeight = 520;
    const radarCenter = { x: thresholdChartWidth / 2, y: 265 };
    const radarRadius = 190;
    const radarRingFractions = [0.2, 0.4, 0.6, 0.8, 1];

    // Value at the outer ring: the largest plotted value across the ticks (rounded up to a
    // tidy number for percentages; for ranks it's the worst rank, which sits at the center).
    const radarMaxValue = useMemo(() => {
        let max = 0;
        Object.values(chartDataThreshold).forEach(values => {
            radarTicks.forEach(t => {
                const v = values[thresholdsListThreshold.indexOf(t)];
                if (v > max) max = v;
            });
        });
        if (showRankThreshold) return Math.max(max, 2);
        return max > 0 ? Math.ceil(max / 5) * 5 : 1;
    }, [chartDataThreshold, radarTicks, thresholdsListThreshold, showRankThreshold]);

    const getRadarAngle = (axisIdx) => -Math.PI / 2 + (2 * Math.PI * axisIdx) / radarTicks.length;

    const getRadarPoint = (axisIdx, radius) => {
        const angle = getRadarAngle(axisIdx);
        return {
            x: radarCenter.x + radius * Math.cos(angle),
            y: radarCenter.y + radius * Math.sin(angle)
        };
    };

    // Distance from the center for a value. In percentage mode, higher values go outward;
    // in rank mode rank 1 (best) is at the outer edge and the worst rank at the center.
    const getRadarRadius = (value) => {
        if (showRankThreshold) {
            return ((radarMaxValue - value) / (radarMaxValue - 1)) * radarRadius;
        }
        return (value / radarMaxValue) * radarRadius;
    };

    return (
        <div>
            <h1 style={{textAlign: 'center', marginTop: '1.2em', marginBottom: '0.2em', fontWeight: 500, fontSize: '2.2em', color: '#fff'}}>Country Rankings</h1>
            <div style={{textAlign: 'center', marginBottom: '2.5em', fontSize: '1.15em', color: '#aaa', fontWeight: 500}}>
                Number of fighters per country in the "pound-for-pound top N" (slide to choose N).
                <br /><span style={{ fontSize: '0.85em' }}>Source data: weight class-specific, ELO-style individual rankings from <a href="https://www.fightmatrix.com/historical-mma-rankings/generated-historical-rankings/" target="_blank" rel="noopener noreferrer" style={{ color: '#98b9dfff', textDecoration: 'none' }}>fightmatrix</a>.</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '3.5em', paddingLeft: '2em', paddingRight: '2em', margin: '0 auto', maxWidth: '1600px', width: '100%', boxSizing: 'border-box' }}>
                {/* Left sidebar with controls */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5em', minWidth: '220px', position: 'sticky', top: '20px', background: '#1a1a1a', padding: '12px', borderRadius: '4px', border: '1px solid #555' }}>
                    {/* Gender Toggle */}
                    <div 
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <div 
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.25s ease'
                            }}
                            onClick={() => { setGender(gender === 'men' ? 'women' : 'men'); fightersCache.current = {}; }}
                        >
                        <span style={{ fontSize: '0.8rem', color: '#ddd', fontWeight: 500, minWidth: '32px', textAlign: 'center' }}>Men</span>
                        <div style={{
                            width: '32px',
                            height: '20px',
                            background: '#444',
                            borderRadius: '10px',
                            position: 'relative',
                            transition: 'all 0.25s ease',
                            border: '1px solid #555'
                        }}>
                            <div style={{
                                width: '16px',
                                height: '16px',
                                background: '#fff',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '2px',
                                left: gender === 'men' ? '2px' : '14px',
                                transition: 'all 0.25s ease'
                            }} />
                        </div>
                        <span style={{ fontSize: '0.8rem', color: '#ddd', fontWeight: 500, minWidth: '32px', textAlign: 'center' }}>Women</span>
                        </div>
                    </div>

                    <label style={{ display: 'flex', flexDirection: 'column', color: '#ddd' }}>
                        <span style={{ marginBottom: 4, fontWeight: 500 }}>Global Rank Threshold: <b>{globalRank}</b></span>
                        <input
                            type="range"
                            min="10"
                            max={getMaxGlobalRank()}
                            step="10"
                            value={globalRank}
                            onChange={e => setGlobalRank(Number(e.target.value))}
                            style={{
                                marginTop: '8px'
                            }}
                        />
                    </label>

                    <label style={{ display: 'flex', flexDirection: 'column', color: '#ddd' }}>
                        <span style={{ marginBottom: 4, fontWeight: 500 }}>Quarterly Issue</span>
                        <select
                            value={issue}
                            onChange={e => {
                                const newIssue = e.target.value;
                                setIssue(newIssue);
                                // The baseline must stay earlier than the selected issue
                                if (compareIssue && Number(compareIssue) >= Number(newIssue)) {
                                    setCompareIssue('');
                                }
                            }}
                            style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #888', width: '100%', background: '#1a1a1a', color: darkMode ? '#f1f1f1' : '#222' }}
                        >
                            <option value="">Select a date...</option>
                            {issueOptions.map(opt => (
                                <option key={opt.issue} value={opt.issue}>
                                    {opt.date}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label style={{ display: 'flex', flexDirection: 'column', color: '#ddd' }}>
                        <span style={{ marginBottom: 4, fontWeight: 500 }}>Compare Rank Changes To</span>
                        <select
                            value={compareIssue}
                            onChange={e => setCompareIssue(e.target.value)}
                            style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #888', width: '100%', background: '#1a1a1a', color: darkMode ? '#f1f1f1' : '#222' }}
                        >
                            <option value="">Previous quarter (default)</option>
                            {issueOptions
                                .filter(opt => issue && Number(opt.issue) < Number(issue))
                                .map(opt => (
                                    <option key={opt.issue} value={opt.issue}>
                                        {opt.date}
                                    </option>
                                ))}
                        </select>
                    </label>

                    {/* Weight Class Filter Checkboxes */}
                    <div style={{ display: 'flex', flexDirection: 'column', color: '#ddd' }}>
                        <span style={{ marginBottom: 6, fontWeight: 500 }}>Weight Classes (default: all)</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '200px', overflowY: 'auto' }}>
                            {allWeightClasses.map(wcId => (
                                <label key={wcId} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', justifyContent: 'space-between', paddingRight: '8px' }}>
                                    <span style={{ fontSize: '0.8em', color: '#ddd', marginLeft: '8px' }}>{weightClassNames[wcId] || wcId}</span>
                                    <div style={{
                                        width: '10px',
                                        height: '10px',
                                        border: '1.5px solid #666',
                                        borderRadius: '2px',
                                        background: selectedWeightClasses.includes(wcId) ? '#666' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s ease',
                                        flexShrink: 0
                                    }} />
                                    <input
                                        type="checkbox"
                                        checked={selectedWeightClasses.includes(wcId)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedWeightClasses([...selectedWeightClasses, wcId]);
                                            } else {
                                                setSelectedWeightClasses(selectedWeightClasses.filter(w => w !== wcId));
                                            }
                                        }}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            ))}
                        </div>
                    </div>
                    
                    {/* Toggle for Russia/North Caucasus split, only show for men */}
                    {gender === 'men' && (
                        <div 
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.25s ease'
                            }}
                            onClick={() => setSplitRussia(!splitRussia)}
                        >
                            <span style={{ fontSize: '0.8rem', color: '#ddd', fontWeight: 500, minWidth: 'auto', textAlign: 'center', flex: 1 }}>Separate North Caucasus</span>
                            <div style={{
                                width: '32px',
                                height: '20px',
                                background: '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '2px',
                                    left: splitRussia ? '14px' : '2px',
                                    transition: 'all 0.25s ease'
                                }} />
                            </div>
                        </div>
                    )}
                    
                    <button 
                        onClick={() => fetchRankings(issue, globalRank)} 
                        style={{ 
                            height: 40,
                            padding: '8px 16px',
                            fontSize: '1em',
                            fontWeight: 600,
                            borderRadius: 6,
                            border: '1px solid #888',
                            background: darkMode ? '#232323' : '#fafafa',
                            color: '#ddd',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            width: 'auto',
                            whiteSpace: 'nowrap',
                            alignSelf: 'center'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = darkMode ? '#1a1a1a' : '#f0f0f0';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = darkMode ? '#1a1a1a' : '#fafafa';
                        }}
                    >
                        Apply
                    </button>
                </div>
                <div className="rankings-tables-wrapper">
                    {/* Main country rankings table */}
                    <div className="main-table-container" style={{ border: '1px solid #555', borderRadius: '4px' }}>
                        {error && <div style={{ color: 'red', marginBottom: '1em' }}>{error}</div>}
                        {rankings.length > 0 && (
                            <table
                                className={`ranking-table${darkMode ? ' dark' : ''}`}
                                style={{ width: '100%', borderCollapse: 'collapse' }}
                            >
                        <thead>
                            <tr style={{ background: darkMode ? '#2a2a2a' : '#efefef', color: darkMode ? '#f1f1f1' : '#222' }}>
                                <th style={{ padding: '8px', fontWeight: 400 }}>#</th>
                                <th style={{ padding: '8px', fontWeight: 400 }}>Country / Region</th>
                                <th style={{ padding: '8px' }}>
                                    <button
                                        onClick={() => handleSort('fighters')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: darkMode ? '#f1f1f1' : '#222',
                                            fontWeight: 400,
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '1em',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            fontFamily: 'inherit'
                                        }}
                                        aria-label="Sort by number of fighters"
                                    >
                                        Number of Fighters
                                        {getSortIcon('fighters')}
                                    </button>
                                </th>
                                <th style={{ padding: '8px' }}>
                                    <button
                                        onClick={() => handleSort('perMillion')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: darkMode ? '#f1f1f1' : '#222',
                                            fontWeight: 400,
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '1em',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            fontFamily: 'inherit'
                                        }}
                                        aria-label="Sort by fighters per million"
                                    >
                                        per million inhab.
                                        {getSortIcon('perMillion')}
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRankings.map((row, idx) => (
                                <React.Fragment key={row.country_code}>
                                    <tr
                                        style={{
                                            background:
                                                splitRussia && isNorthCaucasus(row.country_code)
                                                    ? (darkMode
                                                        ? 'rgba(255, 235, 59, 0.10)'
                                                        : 'rgba(255, 235, 59, 0.13)')
                                                    : (darkMode ? '#1a1a1a' : '#f5f5f5'),
                                            cursor: 'pointer',
                                            borderBottom: selectedCountry === row.country_code ? '2px solid #888' : undefined
                                        }}
                                        onClick={async () => {
                                            if (selectedCountry === row.country_code) {
                                                setSelectedCountry(null);
                                                setCountryFighters([]);
                                                return;
                                            }
                                            setSelectedCountry(row.country_code);
                                            setLoadingFighters(true);
                                            try {
                                                // Check cache first
                                                if (fightersCache.current[row.country_code]) {
                                                    const cached = fightersCache.current[row.country_code];
                                                    // Filter by selected weight classes if any are selected
                                                    const filtered = selectedWeightClasses.length > 0
                                                        ? cached.filter(f => selectedWeightClasses.includes(parseInt(f.division)))
                                                        : cached;
                                                    setCountryFighters(filtered);
                                                    setLoadingFighters(false);
                                                    return;
                                                }
                                                const apiBase = process.env.REACT_APP_API_URL || '';
                                                const weightClassesParam = selectedWeightClasses.length > 0 ? `&weight_classes=${selectedWeightClasses.join(',')}` : '';
                                                if (row.country_code === 'RU-WHOLE') {
                                                    const RU_CODES = [
                                                        'RU', 'RU-CE', 'RU-DA', 'RU-IN', 'RU-SE', 'RU-KB', 'RU-KC'
                                                    ];
                                                    let allFighters = [];
                                                    for (const code of RU_CODES) {
                                                        if (fightersCache.current[code]) {
                                                            allFighters = allFighters.concat(fightersCache.current[code]);
                                                        } else {
                                                            const res = await fetch(`${apiBase}/api/country-fighters?issue=${issue}&global_rank=${globalRank}&country_code=${code}&gender=${gender}${weightClassesParam}`);
                                                            if (res.ok) {
                                                                const data = await res.json();
                                                                if (Array.isArray(data)) {
                                                                    fightersCache.current[code] = data;
                                                                    allFighters = allFighters.concat(data);
                                                                }
                                                            }
                                                        }
                                                    }
                                                    allFighters.sort((a, b) => (a.global_rank ?? Infinity) - (b.global_rank ?? Infinity));
                                                    fightersCache.current['RU-WHOLE'] = allFighters;
                                                    setCountryFighters(allFighters);
                                                } else if (row.country_code === 'EU-NORU') {
                                                    const EUROPE_CODES = [
                                                        'AL','AD','AT','BY','BE','BA','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IS','IE','IT','LV','LI','LT','LU','MT','MD','MC','ME','NL','MK','NO','PL','PT','RO','SM','RS','SK','SI','ES','SE','CH','UA','GB','UK','VA'
                                                    ];
                                                    let allFighters = [];
                                                    for (const code of EUROPE_CODES) {
                                                        if (fightersCache.current[code]) {
                                                            allFighters = allFighters.concat(fightersCache.current[code]);
                                                        } else {
                                                            const res = await fetch(`${apiBase}/api/country-fighters?issue=${issue}&global_rank=${globalRank}&country_code=${code}&gender=${gender}${weightClassesParam}`);
                                                            if (res.ok) {
                                                                const data = await res.json();
                                                                if (Array.isArray(data)) {
                                                                    fightersCache.current[code] = data;
                                                                    allFighters = allFighters.concat(data);
                                                                }
                                                            }
                                                        }
                                                    }
                                                    allFighters.sort((a, b) => (a.global_rank ?? Infinity) - (b.global_rank ?? Infinity));
                                                    fightersCache.current['EU-NORU'] = allFighters;
                                                    setCountryFighters(allFighters);
                                                } else {
                                                    // Normal country
                                                    const res = await fetch(`${apiBase}/api/country-fighters?issue=${issue}&global_rank=${globalRank}&country_code=${row.country_code}&gender=${gender}${weightClassesParam}`);
                                                    const data = await res.json();
                                                    fightersCache.current[row.country_code] = data;
                                                    setCountryFighters(data);
                                                }
                                            } catch (e) {
                                                setCountryFighters([]);
                                            }
                                            setLoadingFighters(false);
                                        }}
                                    >
                                        <td style={{ padding: '8px', textAlign: 'center', color: '#bbb', whiteSpace: 'nowrap' }}>
                                            {getRankNumber(row, idx)}
                                            {(() => {
                                                const change = getRankChange(row);
                                                if (change === null) return null;
                                                if (change === 'new') {
                                                    return <span style={{ marginLeft: 6, fontSize: '0.72em', color: '#4caf50' }}>NEW</span>;
                                                }
                                                if (change === 0) {
                                                    return <span style={{ marginLeft: 6, fontSize: '0.72em', color: '#888' }}>=</span>;
                                                }
                                                const up = change > 0;
                                                return (
                                                    <span style={{ marginLeft: 6, fontSize: '0.72em', color: up ? '#4caf50' : '#e57373' }}>
                                                        {up ? '▲' : '▼'}{Math.abs(change)}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }} onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/country-rankings/${row.country_code}`);
                                            }}>
                                                {/* Show real flag for North Caucasus republics if available, else fallback */}
                                                {isNorthCaucasus(row.country_code) ? (
                                                    <img
                                                        src={`/flags/${row.country_code}.svg`}
                                                        alt={getCountryName(row.country_code) + ' flag'}
                                                        style={{ width: '1.3em', height: '1.3em', marginRight: 6, verticalAlign: 'middle', objectFit: 'contain' }}
                                                        onError={e => { e.target.onerror = null; e.target.src = '/flags/RU.svg'; }}
                                                    />
                                                ) : (
                                                    <CountryFlag
                                                        countryCode={
                                                            row.country_code === 'UK' ? 'GB'
                                                            : row.country_code === 'RU-WHOLE' ? 'RU'
                                                            : row.country_code === 'EU-NORU' ? 'EU'
                                                            : row.country_code
                                                        }
                                                        svg
                                                        style={{ width: '1.3em', height: '1.3em', marginRight: 6, verticalAlign: 'middle' }}
                                                    />
                                                )}
                                                <span style={{ verticalAlign: 'middle', color: '#bbb', transition: 'color 0.2s ease', textDecoration: 'none' }} className="country-name-link" onMouseEnter={(e) => e.target.style.color = '#6ba3ff'} onMouseLeave={(e) => e.target.style.color = '#bbb'}>{getCountryDisplayName(row.country_code === 'UK' ? 'GB' : row.country_code)}</span>
                                            </span>
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'center', color: '#bbb', whiteSpace: 'nowrap' }}>
                                            {row.fighter_count}
                                            {(() => {
                                                const delta = getCountChange(row);
                                                if (!delta) return null;
                                                return (
                                                    <span style={{ marginLeft: 6, fontSize: '0.72em', color: delta > 0 ? '#4caf50' : '#e57373' }}>
                                                        ({delta > 0 ? '+' : '-'}{Math.abs(delta)})
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'center', color: '#bbb' }}>
                                            {(() => {
                                                let pop;
                                                if (row.country_code === 'EU-NORU') {
                                                    pop = row._europePop;
                                                } else {
                                                    const code = row.country_code === 'UK' ? 'GB' : row.country_code;
                                                    pop = countryPopulations[code];
                                                }
                                                if (row.fighter_count < 2 || !pop) return ' ';
                                                return (
                                                    <span style={{ color: '#bbb' }}>
                                                        {(row.fighter_count / (pop / 1_000_000)).toFixed(2)}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                    {selectedCountry === row.country_code && (
                                        <tr>
                                            <td colSpan={4} style={{ background: darkMode ? '#222' : '#f9f9f9', padding: '12px 24px' }}>
                                                {loadingFighters ? (
                                                    <div>Loading...</div>
                                                ) : countryFighters.length > 0 ? (
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                                                        <thead>
                                                            <tr>
                                                                <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 400 }}>#</th>
                                                                <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 400 }}>Name</th>
                                                                <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 400 }}>Division</th>
                                                                <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 400 }}>Division Rank</th>
                                                                <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 400 }}>Global Rank</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {countryFighters.map((f, i) => (
                                                                <tr key={f.name + f.division + f.rank} style={{ borderBottom: '1px solid #444' }}>
                                                                    <td style={{ padding: '4px 8px', color: '#ddd', fontSize: '0.92em' }}>{i + 1}</td>
                                                                    <td style={{ padding: '4px 8px', color: '#ddd', fontSize: '0.92em' }}>
                                                                        {f.profile_link ? (
                                                                            <a
                                                                                href={`https://www.fightmatrix.com${f.profile_link}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="fighter-link"
                                                                                style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                                                                            >
                                                                                {f.name}
                                                                            </a>
                                                                        ) : f.name}
                                                                    </td>
                                                                    <td style={{ padding: '4px 8px', color: '#ddd', fontSize: '0.92em' }}>{f.division}</td>
                                                                    <td style={{ padding: '4px 8px', color: '#ddd', fontSize: '0.92em' }}>{f.rank}</td>
                                                                    <td style={{ padding: '4px 8px', color: '#ddd', fontSize: '0.92em' }}>{f.global_rank}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div>No fighters found for this country.</div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                  )}
                        {rankings.length === 0 && !error && (
                            <div style={{ color: '#ddd', marginTop: '2em' }}>No data to display.</div>
                        )}
                    </div>
                    {/* Max ranks table on the right */}
                    <div className="max-ranks-table-container" style={{ background: '#1a1a1a', padding: '10px 12px 12px 12px', borderRadius: '4px', border: '1px solid #555' }}>
                        {divisionError && divisionRanks.length === 0 && (
                            <div style={{ color: 'red', marginBottom: '1em' }}>{divisionError}</div>
                        )}
                        {divisionRanks.length > 0 ? (() => {
                            const divisionNames = {
                                1: "Heavyweight",
                                2: "L. Heavyweight",
                                3: "Middleweight",
                                4: "Welterweight",
                                5: "Lightweight",
                                6: "Featherweight",
                                7: "Bantamweight",
                                8: "Flyweight",
                                13: "Strawweight",
                                14: "Flyweight",
                                15: "Bantamweight"
                            };
                            const sortedDivs = divisionRanks
                                .map(row => ({ ...row, max_rank: Number(row.max_rank) }))
                                .sort((a, b) => a.division - b.division);
                            const totalMaxRanks = sortedDivs.reduce((sum, row) => sum + row.max_rank, 0);
                            return (
                                <table className="max-ranks-table" style={{ marginTop: '-4px' }}>
                                    <thead>
                                        <tr style={{ background: '#1a1a1a' }}>
                                            <th style={{ padding: '0px', color: '#ddd', fontWeight: '400' }}></th>
                                            <th style={{ padding: '4px 8px', color: '#ddd', fontWeight: '400' }}>Max Rank Included</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedDivs.map((row, idx) => (
                                            <tr key={row.division}>
                                                <td style={{ padding: '8px', textAlign: 'left', color: '#ddd' }}>{divisionNames[row.division] || row.division}</td>
                                                <td style={{ padding: '8px', textAlign: 'center', color: '#ddd' }}>{row.max_rank}</td>
                                            </tr>
                                        ))}
                                        {/* Total row */}
                                        <tr style={{ borderTop: '1px solid #888', marginTop: '8px' }}>
                                            <td style={{ padding: '8px', textAlign: 'left', borderTop: '1px solid #888', color: '#ddd' }}>Total</td>
                                            <td style={{ padding: '8px', textAlign: 'center', borderTop: '1px solid #888', color: '#ddd' }}>{totalMaxRanks}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            );
                        })() : (
                            <div style={{ color: '#ddd', marginTop: '1em' }}>No division data to display.</div>
                        )}
                    </div>
                </div>
            </div>

            <WorldRankingsMap
                rankings={rankings}
                onSelectCountry={(code) => navigate(`/country-rankings/${code}`)}
                filterSummary={[
                    gender === 'men' ? 'Men' : 'Women',
                    `Top ${globalRank}`,
                    issueOptions.find(o => String(o.issue) === String(issue))?.date,
                    selectedWeightClasses.length === allWeightClasses.length
                        ? 'All weight classes'
                        : selectedWeightClasses.map(id => weightClassNames[id] || id).join(', '),
                ].filter(Boolean).join(' · ')}
            />

            {/* === Country Evolution Section === */}
            <div style={{ marginTop: '4em', paddingTop: '2em', borderTop: '1px solid #555' }}>
                <h1 style={{textAlign: 'center', marginTop: '1.2em', marginBottom: '0.2em', fontWeight: 500, fontSize: '2.2em', color: '#fff'}}>Chronological View</h1>
                <div style={{textAlign: 'center', marginBottom: '2.5em', fontSize: '1.15em', color: '#aaa', fontWeight: 500}}>
                    Frequency among the pound-for-pound top N across time.
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '3.5em', paddingLeft: '2em', paddingRight: '2em', margin: '0 auto', maxWidth: '1600px', width: '100%', boxSizing: 'border-box' }}>
                    {/* Left sidebar with evolution controls */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5em', minWidth: '220px', position: 'sticky', top: '20px', background: '#1a1a1a', padding: '12px', borderRadius: '4px', border: '1px solid #555' }}>
                        {/* Gender Toggle */}
                        <div 
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.25s ease',
                                alignSelf: 'center'
                            }}
                            onClick={() => { setGender(gender === 'men' ? 'women' : 'men'); }}
                        >
                            <span style={{ fontSize: '0.8rem', color: '#ccc', fontWeight: 500, minWidth: '32px', textAlign: 'center' }}>Men</span>
                            <div style={{
                                width: '32px',
                                height: '20px',
                                background: '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '2px',
                                    left: gender === 'men' ? '2px' : '14px',
                                    transition: 'all 0.25s ease'
                                }} />
                            </div>
                            <span style={{ fontSize: '0.8rem', color: '#ccc', fontWeight: 500, minWidth: '32px', textAlign: 'center' }}>Women</span>
                        </div>

                        {/* Toggle for Absolute vs Rankings */}
                        <div 
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.25s ease',
                                alignSelf: 'center'
                            }}
                            onClick={() => setShowRankingsEvolution(!showRankingsEvolution)}
                        >
                            <span style={{ fontSize: '0.8rem', color: '#ccc', fontWeight: 500, minWidth: '45px', textAlign: 'center' }}>Proportion</span>
                            <div style={{
                                width: '32px',
                                height: '20px',
                                background: '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '2px',
                                    left: showRankingsEvolution ? '14px' : '2px',
                                    transition: 'all 0.25s ease'
                                }} />
                            </div>
                            <span style={{ fontSize: '0.8rem', color: '#ccc', fontWeight: 500, minWidth: '45px', textAlign: 'center' }}>Rank</span>
                        </div>

                        {/* Global Rank Threshold Slider */}
                        <label style={{ display: 'flex', flexDirection: 'column', color: '#ccc' }}>
                            <span style={{ marginBottom: 4, fontWeight: 500 }}>Global Rank Threshold: <b>{globalRankThresholdEvolution}</b></span>
                            <input
                                type="range"
                                min="10"
                                max="1500"
                                step="10"
                                value={globalRankThresholdEvolution}
                                onChange={e => setGlobalRankThresholdEvolution(Number(e.target.value))}
                                style={{
                                    marginTop: '8px'
                                }}
                            />
                        </label>

                        {/* Date Range Slider */}
                        {issueOptions.length > 0 && (() => {
                            const minIssue = Math.min(...issueOptions.map(o => o.issue));
                            const maxIssue = Math.max(...issueOptions.map(o => o.issue));
                            const startVal = issueRangeMin ?? minIssue;
                            const endVal = issueRangeMax ?? maxIssue;
                            const startLabel = issueOptions.find(o => o.issue === startVal)?.date || startVal;
                            const endLabel = issueOptions.find(o => o.issue === endVal)?.date || endVal;

                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8em' }}>
                                    <label style={{ display: 'flex', flexDirection: 'column', color: '#ccc' }}>
                                        <span style={{ marginBottom: 4, fontWeight: 500 }}>Start: <b>{startLabel}</b></span>
                                        <input
                                            type="range"
                                            min={minIssue}
                                            max={maxIssue}
                                            step="1"
                                            value={startVal}
                                            onChange={e => {
                                                const val = Number(e.target.value);
                                                if (val <= endVal) {
                                                    setIssueRangeMin(val === minIssue ? null : val);
                                                }
                                            }}
                                            style={{
                                                marginTop: '8px'
                                            }}
                                        />
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', color: '#ccc' }}>
                                        <span style={{ marginBottom: 4, fontWeight: 500 }}>End: <b>{endLabel}</b></span>
                                        <input
                                            type="range"
                                            min={minIssue}
                                            max={maxIssue}
                                            step="1"
                                            value={endVal}
                                            onChange={e => {
                                                const val = Number(e.target.value);
                                                if (val >= startVal) {
                                                    setIssueRangeMax(val === maxIssue ? null : val);
                                                }
                                            }}
                                            style={{
                                                marginTop: '8px'
                                            }}
                                        />
                                    </label>
                                </div>
                            );
                        })()}

                        {/* Country Search Bar */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '8px',
                            position: 'relative',
                            width: '100%'
                        }}>
                            <label style={{ color: '#ccc', fontSize: '14px', fontWeight: '600' }}>Add Countries</label>
                            <div style={{ position: 'relative', width: '100%' }}>
                                <input
                                    type="text"
                                    placeholder="Search countries..."
                                    value={searchInputEvolution}
                                    onChange={(e) => {
                                        setSearchInputEvolution(e.target.value);
                                        setShowSuggestionsEvolution(true);
                                    }}
                                    onFocus={() => setShowSuggestionsEvolution(true)}
                                    onBlur={() => setTimeout(() => setShowSuggestionsEvolution(false), 200)}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        backgroundColor: '#333',
                                        color: '#ccc',
                                        border: '1px solid #555',
                                        borderRadius: '4px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s ease',
                                        boxSizing: 'border-box'
                                    }}
                                />
                                {showSuggestionsEvolution && getFilteredCountriesEvolution().length > 0 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: '#333',
                                        border: '1px solid #555',
                                        borderTop: 'none',
                                        borderRadius: '0 0 4px 4px',
                                        maxHeight: '250px',
                                        overflowY: 'auto',
                                        zIndex: 1000
                                    }}>
                                        {getFilteredCountriesEvolution().map(country => {
                                            const isSelected = selectedCountriesEvolution.includes(country);
                                            return (
                                                <div
                                                    key={country}
                                                    style={{
                                                        padding: '8px 12px',
                                                        color: '#ccc',
                                                        fontSize: '13px',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s ease',
                                                        borderBottom: '1px solid #444',
                                                        opacity: isSelected ? 0.5 : 1,
                                                        backgroundColor: isSelected ? '#2a2a2a' : 'transparent'
                                                    }}
                                                    onClick={() => addCountryEvolution(country)}
                                                    onMouseEnter={(e) => {
                                                        if (!isSelected) {
                                                            e.currentTarget.style.background = '#444';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.background = isSelected ? '#2a2a2a' : 'transparent';
                                                    }}
                                                >
                                                    {getCountryName(country)} ({country})
                                                    {isSelected && <span style={{ marginLeft: '8px', color: '#4ECDC4' }}>✓</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Apply Button */}
                        <button
                            onClick={fetchEvolutionData}
                            disabled={selectedCountriesEvolution.length === 0 || loadingEvolution}
                            style={{ 
                                height: 40,
                                padding: '8px 16px',
                                fontSize: '1em',
                                fontWeight: 600,
                                borderRadius: 6,
                                border: '1px solid #888',
                                background: '#232323',
                                color: '#ddd',
                                cursor: selectedCountriesEvolution.length === 0 || loadingEvolution ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                width: 'auto',
                                whiteSpace: 'nowrap',
                                opacity: selectedCountriesEvolution.length === 0 || loadingEvolution ? 0.6 : 1,
                                alignSelf: 'center'
                            }}
                            onMouseEnter={(e) => {
                                if (selectedCountriesEvolution.length > 0 && !loadingEvolution) {
                                    e.target.style.background = '#1a1a1a';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (selectedCountriesEvolution.length > 0 && !loadingEvolution) {
                                    e.target.style.background = '#232323';
                                }
                            }}
                        >
                            {loadingEvolution ? 'Loading...' : 'Apply'}
                        </button>
                    </div>

                    {/* Chart and content on the right */}
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '2em', alignItems: 'flex-start', flex: 1 }}>
                        {/* SVG Chart */}
                        <div>
                            {dataEvolution.length > 0 && !loadingEvolution && (
                                <svg width={900} height={520} style={{ display: 'block' }} onMouseMove={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setCursorPosEvolution({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                                }}>
                                {/* Grid lines for Y-axis */}
                                <g>
                                    {[0, 0.25, 0.5, 0.75, 1].map((proportion) => {
                                        const y = 40 + 420 - proportion * 420;
                                        const maxVal = getMaxProportionEvolution();
                                        const displayValue = showRankingsEvolution 
                                            ? Math.round(maxVal - proportion * maxVal)
                                            : Math.round(proportion * maxVal);
                                        return (
                                            <g key={`grid-${proportion}`}>
                                                <line
                                                    x1={60}
                                                    y1={y}
                                                    x2={720}
                                                    y2={y}
                                                    stroke="#444"
                                                    strokeWidth="1"
                                                    strokeDasharray="2 2"
                                                />
                                                <text
                                                    x={50}
                                                    y={y + 4}
                                                    textAnchor="end"
                                                    fill="#999"
                                                    fontSize="11"
                                                >
                                                    {displayValue}{showRankingsEvolution ? '' : '%'}
                                                </text>
                                            </g>
                                        );
                                    })}
                                </g>

                                {/* Y-axis label */}
                                <text
                                    x={-260}
                                    y={15}
                                    textAnchor="middle"
                                    fill="#fff"
                                    fontSize="14"
                                    fontWeight="600"
                                    transform={`rotate(-90, ${15}, ${260})`}
                                >
                                    {showRankingsEvolution ? 'Ranking' : 'Fighter Proportion (%)'}
                                </text>

                                {/* X-axis labels (January years only, every 5 years) */}
                                <g>
                                    {chartDataEvolution.issues.filter((issue) => {
                                        const dateLabel = chartDataEvolution.issueToDateMap[issue] || '';
                                        if (!dateLabel.includes('Jan')) return false;
                                        const year = parseInt(dateLabel.split(' ')[1]);
                                        return year % 5 === 0;
                                    }).map((issue) => {
                                        const idx = chartDataEvolution.issues.indexOf(issue);
                                        const dateLabel = chartDataEvolution.issueToDateMap[issue] || `Issue ${issue}`;
                                        const year = dateLabel.split(' ')[1];
                                        return (
                                            <text
                                                key={`label-${issue}`}
                                                x={getXEvolution(idx)}
                                                y={500}
                                                textAnchor="middle"
                                                fill="#ccc"
                                                fontSize="12"
                                            >
                                                {year}
                                            </text>
                                        );
                                    })}
                                </g>

                                {/* X-axis tick marks (January years only, every 5 years) */}
                                <g>
                                    {chartDataEvolution.issues.filter((issue) => {
                                        const dateLabel = chartDataEvolution.issueToDateMap[issue] || '';
                                        if (!dateLabel.includes('Jan')) return false;
                                        const year = parseInt(dateLabel.split(' ')[1]);
                                        return year % 5 === 0;
                                    }).map((issue) => {
                                        const idx = chartDataEvolution.issues.indexOf(issue);
                                        return (
                                            <line
                                                key={`tick-${issue}`}
                                                x1={getXEvolution(idx)}
                                                y1={460}
                                                x2={getXEvolution(idx)}
                                                y2={465}
                                                stroke="#ccc"
                                                strokeWidth="1.5"
                                            />
                                        );
                                    })}
                                </g>

                                {/* Lines for each country */}
                                {selectedCountriesEvolution.map((countryCode) => {
                                    const color = getCountryColorEvolution(countryCode);
                                    
                                    const allPoints = chartDataEvolution.issues
                                        .map((issue, idx) => {
                                            const proportion = chartDataEvolution.issueMap[issue]?.[countryCode] || 0;
                                            return { issue, idx, proportion, x: getXEvolution(idx), y: getYEvolution(proportion) };
                                        });

                                    const visiblePoints = allPoints.filter(p => p.proportion > 0);
                                    if (visiblePoints.length === 0) return null;

                                    const points = allPoints;

                                    const pathData = points
                                        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
                                        .join(' ');

                                    const lastVisiblePoint = visiblePoints[visiblePoints.length - 1];
                                    const isHighlighted = hoveredCountryEvolution === null || hoveredCountryEvolution === countryCode;
                                    const opacity = isHighlighted ? 1 : 0.2;

                                    return (
                                        <g key={`country-${countryCode}`}>
                                            {/* Line */}
                                            <path
                                                d={pathData}
                                                stroke={color}
                                                strokeWidth="1.5"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                opacity={opacity}
                                                style={{ transition: 'opacity 0.2s ease' }}
                                            />
                                            
                                            {/* Data points */}
                                            {visiblePoints.map((point, idx) => {
                                                const isIssueHovered = hoveredIssueEvolution === point.issue;
                                                return (
                                                    <circle
                                                        key={`point-${countryCode}-${idx}`}
                                                        cx={point.x}
                                                        cy={point.y}
                                                        r={isIssueHovered ? "5" : "2"}
                                                        fill={color}
                                                        opacity={opacity}
                                                        style={{ transition: 'opacity 0.2s ease', cursor: 'pointer' }}
                                                        onMouseEnter={() => setHoveredIssueEvolution(point.issue)}
                                                        onMouseLeave={() => setHoveredIssueEvolution(null)}
                                                    />
                                                );
                                            })}
                                            
                                            {/* Label at end of line */}
                                            <line
                                                x1={lastVisiblePoint.x}
                                                y1={lastVisiblePoint.y}
                                                x2={730}
                                                y2={lastVisiblePoint.y}
                                                stroke="#666"
                                                strokeWidth="1"
                                                strokeDasharray="2 2"
                                                opacity={opacity}
                                                style={{ transition: 'opacity 0.2s ease' }}
                                            />
                                            <text
                                                x={735}
                                                y={lastVisiblePoint.y + 4}
                                                fill={color}
                                                fontSize="12"
                                                fontWeight="400"
                                                opacity={opacity}
                                                style={{
                                                    cursor: 'pointer',
                                                    transition: 'opacity 0.2s ease'
                                                }}
                                                onMouseEnter={() => setHoveredCountryEvolution(countryCode)}
                                                onMouseLeave={() => setHoveredCountryEvolution(null)}
                                            >
                                                {getCountryName(countryCode)}
                                            </text>
                                        </g>
                                    );
                                })}

                                {/* Hover zones for each issue */}
                                {chartDataEvolution.issues.map((issue, idx) => {
                                    const x = getXEvolution(idx);
                                    
                                    let zoneX, zoneWidth;
                                    if (idx === 0) {
                                        const nextX = idx < chartDataEvolution.issues.length - 1 ? getXEvolution(idx + 1) : 720;
                                        zoneX = 60;
                                        zoneWidth = (x + nextX) / 2 - 60;
                                    } else if (idx === chartDataEvolution.issues.length - 1) {
                                        const prevX = getXEvolution(idx - 1);
                                        zoneX = (prevX + x) / 2;
                                        zoneWidth = 720 - zoneX;
                                    } else {
                                        const prevX = getXEvolution(idx - 1);
                                        const nextX = getXEvolution(idx + 1);
                                        zoneX = (prevX + x) / 2;
                                        zoneWidth = (x + nextX) / 2 - zoneX;
                                    }
                                    
                                    return (
                                        <rect
                                            key={`zone-${issue}`}
                                            x={zoneX}
                                            y={40}
                                            width={zoneWidth}
                                            height={420}
                                            fill="transparent"
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={() => setHoveredIssueEvolution(issue)}
                                            onMouseLeave={() => setHoveredIssueEvolution(null)}
                                        />
                                    );
                                })}

                                {/* Vertical line and tooltip when hovering */}
                                {hoveredIssueEvolution !== null && (() => {
                                    const hoveredIdx = chartDataEvolution.issues.indexOf(hoveredIssueEvolution);
                                    if (hoveredIdx === -1) return null;
                                    
                                    const tooltipData = selectedCountriesEvolution.map(country => ({
                                        country,
                                        proportion: chartDataEvolution.issueMap[hoveredIssueEvolution]?.[country] || 0
                                    })).filter(d => d.proportion > 0);

                                    const dateLabel = chartDataEvolution.issueToDateMap[hoveredIssueEvolution] || `Issue ${hoveredIssueEvolution}`;
                                    const tooltipWidth = 150;
                                    
                                    // Calculate height based on number of countries (with more spacing)
                                    let lineCount = 1; // title
                                    tooltipData.forEach(() => lineCount++);
                                    const tooltipHeight = 35 + (lineCount * 16);
                                    
                                    // Use cursor position for tooltip, with offset
                                    let tooltipX = cursorPosEvolution.x + 15;
                                    let tooltipY = cursorPosEvolution.y - tooltipHeight - 10;
                                    
                                    // Keep tooltip within bounds
                                    if (tooltipX + tooltipWidth > 900) {
                                        tooltipX = 900 - tooltipWidth - 10;
                                    }
                                    if (tooltipY < 0) {
                                        tooltipY = cursorPosEvolution.y + 15;
                                    }

                                    return (
                                        <g>
                                            {/* Vertical line */}
                                            <line
                                                x1={getXEvolution(hoveredIdx)}
                                                y1={40}
                                                x2={getXEvolution(hoveredIdx)}
                                                y2={460}
                                                stroke="#888"
                                                strokeWidth="1.5"
                                                strokeDasharray="4 2"
                                            />

                                            {/* Tooltip background */}
                                            <rect
                                                x={tooltipX}
                                                y={tooltipY}
                                                width={tooltipWidth}
                                                height={tooltipHeight}
                                                fill="#1a1a1a"
                                                stroke="#555"
                                                strokeWidth="1"
                                                rx="8"
                                                ry="8"
                                            />

                                            {/* Tooltip content */}
                                            <text
                                                x={tooltipX + 10}
                                                y={tooltipY + 18}
                                                fill="#fff"
                                                fontSize="12"
                                                fontWeight="700"
                                            >
                                                {dateLabel}
                                            </text>

                                            {tooltipData.map((d, idx) => (
                                                <g key={`tooltip-${d.country}`}>
                                                    <rect
                                                        x={tooltipX + 8}
                                                        y={tooltipY + 45 + idx * 16 - 8}
                                                        width="6"
                                                        height="6"
                                                        fill={getCountryColorEvolution(d.country)}
                                                        rx="1"
                                                    />
                                                    <text
                                                        x={tooltipX + 18}
                                                        y={tooltipY + 45 + idx * 16}
                                                        fill="#ccc"
                                                        fontSize="11"
                                                    >
                                                        {getCountryName(d.country)}
                                                    </text>
                                                    <text
                                                        x={tooltipX + tooltipWidth - 8}
                                                        y={tooltipY + 45 + idx * 16}
                                                        fill="#ccc"
                                                        fontSize="11"
                                                        textAnchor="end"
                                                    >
                                                        {showRankingsEvolution ? `#${d.proportion}` : `${d.proportion.toFixed(1)}%`}
                                                    </text>
                                                </g>
                                            ))}
                                        </g>
                                    );
                                })()}

                                {/* Axes */}
                                <line
                                    x1={60}
                                    y1={40}
                                    x2={60}
                                    y2={460}
                                    stroke="#ccc"
                                    strokeWidth="2"
                                />
                                <line
                                    x1={60}
                                    y1={460}
                                    x2={720}
                                    y2={460}
                                    stroke="#ccc"
                                    strokeWidth="2"
                                />
                            </svg>
                            )}
                        </div>

                        {/* Selected Countries List for Evolution - now on the right */}
                        {dataEvolution.length > 0 && selectedCountriesEvolution.length > 0 && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                                padding: '16px',
                                backgroundColor: '#1a1a1a',
                                border: '1px solid #555',
                                borderRadius: '8px',
                                minWidth: '200px',
                                maxWidth: '220px',
                                maxHeight: '520px',
                                overflowY: 'auto'
                            }}>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '13px',
                                    fontWeight: '200',
                                    color: '#fff',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>Selected Countries</h3>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px'
                                }}>
                                    {selectedCountriesEvolution.map(country => (
                                        <div key={country} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '8px',
                                            padding: '8px 10px',
                                            backgroundColor: '#232323',
                                            border: '1px solid #444',
                                            borderRadius: '4px',
                                            transition: 'border-color 0.2s ease'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                flex: 1,
                                                minWidth: 0
                                            }}>
                                                <div style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    backgroundColor: getCountryColorEvolution(country),
                                                    borderRadius: '2px',
                                                    flexShrink: 0
                                                }} />
                                                <span style={{
                                                    color: '#ccc',
                                                    fontSize: '13px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {getCountryName(country)}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => removeCountryEvolution(country)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#999',
                                                    fontSize: '20px',
                                                    cursor: 'pointer',
                                                    padding: '0',
                                                    height: '20px',
                                                    width: '20px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'color 0.2s ease',
                                                    flexShrink: 0,
                                                    lineHeight: 1
                                                }}
                                                onMouseEnter={(e) => e.target.style.color = '#ccc'}
                                                onMouseLeave={(e) => e.target.style.color = '#999'}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Empty state and loading/error messages */}
                    <div style={{ flex: 1 }}>
                        {!loadingEvolution && dataEvolution.length === 0 && !errorEvolution && (
                            <div style={{
                                fontSize: '16px',
                                color: '#999',
                                textAlign: 'center',
                                padding: '60px 20px',
                                flex: 1
                            }}>
                                Select countries and click "Apply" to view the chart
                            </div>
                        )}

                        {loadingEvolution && (
                            <div style={{
                                fontSize: '16px',
                                color: '#4ECDC4',
                                textAlign: 'center',
                                padding: '60px 20px',
                                flex: 1
                            }}>
                                Loading data...
                            </div>
                        )}

                        {errorEvolution && (
                            <div style={{
                                fontSize: '16px',
                                color: '#FF6B6B',
                                textAlign: 'center',
                                padding: '20px',
                                backgroundColor: 'rgba(255, 107, 107, 0.1)',
                                borderRadius: '4px',
                                border: '1px solid #FF6B6B',
                                flex: 1
                            }}>
                                {errorEvolution}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* === Rank Threshold Section === */}
            <div style={{ marginTop: '4em', paddingTop: '2em', borderTop: '1px solid #555' }}>
                <h1 style={{textAlign: 'center', marginTop: '1.2em', marginBottom: '0.2em', fontWeight: 500, fontSize: '2.2em', color: '#fff'}}>Fighters by Rank Threshold</h1>
                <div style={{textAlign: 'center', marginBottom: '2.5em', fontSize: '1.15em', color: '#aaa', fontWeight: 500}}>
                    Proportion of fighters from a country in the pound-for-pound top N, at each of several values of N (one axis per threshold), for a single quarterly issue.
                </div>

                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '3.5em', paddingLeft: '2em', paddingRight: '2em', margin: '0 auto', maxWidth: '1600px', width: '100%', boxSizing: 'border-box' }}>
                    {/* Left sidebar with controls */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5em', minWidth: '220px', position: 'sticky', top: '20px', background: '#1a1a1a', padding: '12px', borderRadius: '4px', border: '1px solid #555' }}>
                        {/* Gender Toggle */}
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.25s ease',
                                alignSelf: 'center'
                            }}
                            onClick={() => setGender(gender === 'men' ? 'women' : 'men')}
                        >
                            <span style={{ fontSize: '0.8rem', color: '#ccc', fontWeight: 500, minWidth: '32px', textAlign: 'center' }}>Men</span>
                            <div style={{
                                width: '32px',
                                height: '20px',
                                background: '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '2px',
                                    left: gender === 'men' ? '2px' : '14px',
                                    transition: 'all 0.25s ease'
                                }} />
                            </div>
                            <span style={{ fontSize: '0.8rem', color: '#ccc', fontWeight: 500, minWidth: '32px', textAlign: 'center' }}>Women</span>
                        </div>

                        {/* Toggle for Density (%) vs. Rank */}
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.25s ease',
                                alignSelf: 'center'
                            }}
                            onClick={() => setShowRankThreshold(!showRankThreshold)}
                        >
                            <span style={{ fontSize: '0.8rem', color: '#ccc', fontWeight: 500, minWidth: '45px', textAlign: 'center' }}>Density</span>
                            <div style={{
                                width: '32px',
                                height: '20px',
                                background: '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '2px',
                                    left: showRankThreshold ? '14px' : '2px',
                                    transition: 'all 0.25s ease'
                                }} />
                            </div>
                            <span style={{ fontSize: '0.8rem', color: '#ccc', fontWeight: 500, minWidth: '45px', textAlign: 'center' }}>Rank</span>
                        </div>

                        {/* Quarterly Issue */}
                        <label style={{ display: 'flex', flexDirection: 'column', color: '#ccc' }}>
                            <span style={{ marginBottom: 4, fontWeight: 500 }}>Quarterly Issue</span>
                            <select
                                value={issueThreshold ?? ''}
                                onChange={e => setIssueThreshold(e.target.value)}
                                style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #888', width: '100%', background: '#1a1a1a', color: darkMode ? '#f1f1f1' : '#222' }}
                            >
                                <option value="">Select a date...</option>
                                {issueOptions.map(opt => (
                                    <option key={opt.issue} value={opt.issue}>
                                        {opt.date}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {/* Country Search Bar */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '8px',
                            position: 'relative',
                            width: '100%'
                        }}>
                            <label style={{ color: '#ccc', fontSize: '14px', fontWeight: '600' }}>Add Countries</label>
                            <div style={{ position: 'relative', width: '100%' }}>
                                <input
                                    type="text"
                                    placeholder="Search countries..."
                                    value={searchInputThreshold}
                                    onChange={(e) => {
                                        setSearchInputThreshold(e.target.value);
                                        setShowSuggestionsThreshold(true);
                                    }}
                                    onFocus={() => setShowSuggestionsThreshold(true)}
                                    onBlur={() => setTimeout(() => setShowSuggestionsThreshold(false), 200)}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        backgroundColor: '#333',
                                        color: '#ccc',
                                        border: '1px solid #555',
                                        borderRadius: '4px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s ease',
                                        boxSizing: 'border-box'
                                    }}
                                />
                                {showSuggestionsThreshold && getFilteredCountriesThreshold().length > 0 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: '#333',
                                        border: '1px solid #555',
                                        borderTop: 'none',
                                        borderRadius: '0 0 4px 4px',
                                        maxHeight: '250px',
                                        overflowY: 'auto',
                                        zIndex: 1000
                                    }}>
                                        {getFilteredCountriesThreshold().map(country => {
                                            const isSelected = selectedCountriesThreshold.includes(country);
                                            return (
                                                <div
                                                    key={country}
                                                    style={{
                                                        padding: '8px 12px',
                                                        color: '#ccc',
                                                        fontSize: '13px',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s ease',
                                                        borderBottom: '1px solid #444',
                                                        opacity: isSelected ? 0.5 : 1,
                                                        backgroundColor: isSelected ? '#2a2a2a' : 'transparent'
                                                    }}
                                                    onClick={() => addCountryThreshold(country)}
                                                    onMouseEnter={(e) => {
                                                        if (!isSelected) {
                                                            e.currentTarget.style.background = '#444';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.background = isSelected ? '#2a2a2a' : 'transparent';
                                                    }}
                                                >
                                                    {getCountryName(country)} ({country})
                                                    {isSelected && <span style={{ marginLeft: '8px', color: '#4ECDC4' }}>✓</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Apply Button */}
                        <button
                            onClick={() => fetchThresholdData(selectedCountriesThreshold, issueThreshold)}
                            disabled={selectedCountriesThreshold.length === 0 || !issueThreshold || loadingThreshold}
                            style={{
                                height: 40,
                                padding: '8px 16px',
                                fontSize: '1em',
                                fontWeight: 600,
                                borderRadius: 6,
                                border: '1px solid #888',
                                background: '#232323',
                                color: '#ddd',
                                cursor: selectedCountriesThreshold.length === 0 || !issueThreshold || loadingThreshold ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                width: 'auto',
                                whiteSpace: 'nowrap',
                                opacity: selectedCountriesThreshold.length === 0 || !issueThreshold || loadingThreshold ? 0.6 : 1,
                                alignSelf: 'center'
                            }}
                            onMouseEnter={(e) => {
                                if (selectedCountriesThreshold.length > 0 && issueThreshold && !loadingThreshold) {
                                    e.target.style.background = '#1a1a1a';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (selectedCountriesThreshold.length > 0 && issueThreshold && !loadingThreshold) {
                                    e.target.style.background = '#232323';
                                }
                            }}
                        >
                            {loadingThreshold ? 'Loading...' : 'Apply'}
                        </button>
                    </div>

                    {/* Chart and content on the right */}
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '2em', alignItems: 'flex-start', flex: 1 }}>
                        {/* SVG Chart */}
                        <div>
                            {selectedCountriesThreshold.length > 0 && Object.keys(dataThreshold).length > 0 && !loadingThreshold && (
                                <svg width={thresholdChartWidth} height={thresholdChartHeight} style={{ display: 'block' }} onMouseMove={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setCursorPosThreshold({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                                }}>
                                    {/* Concentric grid rings (polygons, one vertex per threshold axis) with value labels along the top axis */}
                                    <g>
                                        {radarRingFractions.map((frac) => {
                                            const ringPoints = radarTicks
                                                .map((_, i) => {
                                                    const p = getRadarPoint(i, frac * radarRadius);
                                                    return `${p.x},${p.y}`;
                                                })
                                                .join(' ');
                                            const labelValue = showRankThreshold
                                                ? Math.round(radarMaxValue - frac * (radarMaxValue - 1))
                                                : frac * radarMaxValue;
                                            return (
                                                <g key={`ring-${frac}`}>
                                                    <polygon
                                                        points={ringPoints}
                                                        fill="none"
                                                        stroke="#444"
                                                        strokeWidth="1"
                                                        strokeDasharray="2 2"
                                                    />
                                                    <text
                                                        x={radarCenter.x + 5}
                                                        y={radarCenter.y - frac * radarRadius - 3}
                                                        textAnchor="start"
                                                        fill="#999"
                                                        fontSize="10"
                                                    >
                                                        {showRankThreshold ? `#${labelValue}` : `${labelValue.toFixed(1)}%`}
                                                    </text>
                                                </g>
                                            );
                                        })}
                                    </g>

                                    {/* Radial axes and threshold labels, one per x-axis tick */}
                                    <g>
                                        {radarTicks.map((t, i) => {
                                            const end = getRadarPoint(i, radarRadius);
                                            const label = getRadarPoint(i, radarRadius + 16);
                                            const cos = Math.cos(getRadarAngle(i));
                                            const sin = Math.sin(getRadarAngle(i));
                                            const isHovered = hoveredThresholdIdx !== null && thresholdsListThreshold[hoveredThresholdIdx] === t;
                                            return (
                                                <g key={`axis-${t}`}>
                                                    <line
                                                        x1={radarCenter.x}
                                                        y1={radarCenter.y}
                                                        x2={end.x}
                                                        y2={end.y}
                                                        stroke={isHovered ? '#aaa' : '#666'}
                                                        strokeWidth={isHovered ? 1.5 : 1}
                                                    />
                                                    <text
                                                        x={label.x}
                                                        y={label.y + 4 + sin * 6}
                                                        textAnchor={cos > 0.3 ? 'start' : cos < -0.3 ? 'end' : 'middle'}
                                                        fill={isHovered ? '#fff' : '#ccc'}
                                                        fontSize="12"
                                                        fontWeight={isHovered ? 600 : 400}
                                                    >
                                                        Top {t}
                                                    </text>
                                                </g>
                                            );
                                        })}
                                    </g>

                                    {/* Radial axis caption */}
                                    <text x={12} y={thresholdChartHeight - 12} fill="#999" fontSize="11">
                                        {showRankThreshold ? 'Radius: country rank (center = lowest, edge = #1)' : 'Radius: fighters / threshold (%)'}
                                    </text>

                                    {/* One polygon per country */}
                                    {selectedCountriesThreshold.map((countryCode) => {
                                        const color = getCountryColorThreshold(countryCode);
                                        const values = chartDataThreshold[countryCode] || [];
                                        if (values.length === 0) return null;

                                        const points = radarTicks.map((t, i) => {
                                            const value = values[thresholdsListThreshold.indexOf(t)] ?? 0;
                                            return { t, ...getRadarPoint(i, getRadarRadius(value)) };
                                        });
                                        const polygonPoints = points.map(p => `${p.x},${p.y}`).join(' ');

                                        const isHighlighted = hoveredCountryThreshold === null || hoveredCountryThreshold === countryCode;
                                        const opacity = isHighlighted ? 1 : 0.2;

                                        return (
                                            <g key={`country-${countryCode}`} opacity={opacity} style={{ transition: 'opacity 0.2s ease' }}>
                                                <polygon
                                                    points={polygonPoints}
                                                    fill={color}
                                                    fillOpacity="0.12"
                                                    stroke={color}
                                                    strokeWidth="1.5"
                                                    strokeLinejoin="round"
                                                />
                                                {points.map(p => (
                                                    <circle key={`vertex-${countryCode}-${p.t}`} cx={p.x} cy={p.y} r="3" fill={color} />
                                                ))}
                                            </g>
                                        );
                                    })}

                                    {/* Wide, invisible hover zones along each axis */}
                                    {radarTicks.map((t, i) => {
                                        const end = getRadarPoint(i, radarRadius + 12);
                                        return (
                                            <line
                                                key={`zone-${t}`}
                                                x1={radarCenter.x}
                                                y1={radarCenter.y}
                                                x2={end.x}
                                                y2={end.y}
                                                stroke="transparent"
                                                strokeWidth="28"
                                                strokeLinecap="round"
                                                style={{ cursor: 'pointer' }}
                                                onMouseEnter={() => setHoveredThresholdIdx(thresholdsListThreshold.indexOf(t))}
                                                onMouseLeave={() => setHoveredThresholdIdx(null)}
                                            />
                                        );
                                    })}

                                    {/* Tooltip when hovering an axis */}
                                    {hoveredThresholdIdx !== null && (() => {
                                        const t = thresholdsListThreshold[hoveredThresholdIdx];
                                        const tooltipData = selectedCountriesThreshold.map(country => {
                                            const ranks = dataThreshold[country] || [];
                                            return {
                                                country,
                                                percent: (chartDataThreshold[country] || [])[hoveredThresholdIdx] ?? 0,
                                                count: ranks.filter(r => r <= t).length
                                            };
                                        }).sort((a, b) => b.percent - a.percent);

                                        const tooltipWidth = 190;
                                        const lineCount = 1 + tooltipData.length;
                                        const tooltipHeight = 35 + (lineCount * 16);

                                        let tooltipX = cursorPosThreshold.x + 15;
                                        let tooltipY = cursorPosThreshold.y - tooltipHeight - 10;

                                        if (tooltipX + tooltipWidth > thresholdChartWidth) {
                                            tooltipX = thresholdChartWidth - tooltipWidth - 10;
                                        }
                                        if (tooltipY < 0) {
                                            tooltipY = cursorPosThreshold.y + 15;
                                        }

                                        return (
                                            <g pointerEvents="none">
                                                <rect
                                                    x={tooltipX}
                                                    y={tooltipY}
                                                    width={tooltipWidth}
                                                    height={tooltipHeight}
                                                    fill="#1a1a1a"
                                                    stroke="#555"
                                                    strokeWidth="1"
                                                    rx="8"
                                                    ry="8"
                                                />
                                                <text
                                                    x={tooltipX + 10}
                                                    y={tooltipY + 18}
                                                    fill="#fff"
                                                    fontSize="12"
                                                    fontWeight="700"
                                                >
                                                    Top {t}
                                                </text>
                                                {tooltipData.map((d, idx) => (
                                                    <g key={`tooltip-${d.country}`}>
                                                        <rect
                                                            x={tooltipX + 8}
                                                            y={tooltipY + 45 + idx * 16 - 8}
                                                            width="6"
                                                            height="6"
                                                            fill={getCountryColorThreshold(d.country)}
                                                            rx="1"
                                                        />
                                                        <text
                                                            x={tooltipX + 18}
                                                            y={tooltipY + 45 + idx * 16}
                                                            fill="#ccc"
                                                            fontSize="11"
                                                        >
                                                            {getCountryName(d.country)}
                                                        </text>
                                                        <text
                                                            x={tooltipX + tooltipWidth - 8}
                                                            y={tooltipY + 45 + idx * 16}
                                                            fill="#ccc"
                                                            fontSize="11"
                                                            textAnchor="end"
                                                        >
                                                            {showRankThreshold ? `#${d.percent} (${d.count})` : `${d.percent.toFixed(1)}% (${d.count})`}
                                                        </text>
                                                    </g>
                                                ))}
                                            </g>
                                        );
                                    })()}
                                </svg>
                            )}
                        </div>

                        {/* Selected Countries List */}
                        {selectedCountriesThreshold.length > 0 && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                                padding: '16px',
                                backgroundColor: '#1a1a1a',
                                border: '1px solid #555',
                                borderRadius: '8px',
                                minWidth: '200px',
                                maxWidth: '220px',
                                maxHeight: '520px',
                                overflowY: 'auto'
                            }}>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '13px',
                                    fontWeight: '200',
                                    color: '#fff',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>Selected Countries</h3>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px'
                                }}>
                                    {selectedCountriesThreshold.map(country => (
                                        <div key={country}
                                            onMouseEnter={() => setHoveredCountryThreshold(country)}
                                            onMouseLeave={() => setHoveredCountryThreshold(null)}
                                            style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '8px',
                                            padding: '8px 10px',
                                            backgroundColor: '#232323',
                                            border: '1px solid #444',
                                            borderRadius: '4px',
                                            transition: 'border-color 0.2s ease'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                flex: 1,
                                                minWidth: 0
                                            }}>
                                                <div style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    backgroundColor: getCountryColorThreshold(country),
                                                    borderRadius: '2px',
                                                    flexShrink: 0
                                                }} />
                                                <span style={{
                                                    color: '#ccc',
                                                    fontSize: '13px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {getCountryName(country)}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => removeCountryThreshold(country)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#999',
                                                    fontSize: '20px',
                                                    cursor: 'pointer',
                                                    padding: '0',
                                                    height: '20px',
                                                    width: '20px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'color 0.2s ease',
                                                    flexShrink: 0,
                                                    lineHeight: 1
                                                }}
                                                onMouseEnter={(e) => e.target.style.color = '#ccc'}
                                                onMouseLeave={(e) => e.target.style.color = '#999'}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Empty state and loading/error messages */}
                    <div style={{ flex: 1 }}>
                        {!loadingThreshold && selectedCountriesThreshold.length === 0 && !errorThreshold && (
                            <div style={{
                                fontSize: '16px',
                                color: '#999',
                                textAlign: 'center',
                                padding: '60px 20px',
                                flex: 1
                            }}>
                                Select countries and click "Apply" to view the chart
                            </div>
                        )}

                        {loadingThreshold && (
                            <div style={{
                                fontSize: '16px',
                                color: '#4ECDC4',
                                textAlign: 'center',
                                padding: '60px 20px',
                                flex: 1
                            }}>
                                Loading data...
                            </div>
                        )}

                        {errorThreshold && (
                            <div style={{
                                fontSize: '16px',
                                color: '#FF6B6B',
                                textAlign: 'center',
                                padding: '20px',
                                backgroundColor: 'rgba(255, 107, 107, 0.1)',
                                borderRadius: '4px',
                                border: '1px solid #FF6B6B',
                                flex: 1
                            }}>
                                {errorThreshold}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* === Country vs. Country Section === */}
            <CountryMatchups />

            {/* Lorem Ipsum Section */}
            <div style={{ marginTop: '6em', paddingBottom: '4em', paddingLeft: '2em', paddingRight: '2em', maxWidth: '1200px', margin: '6em auto 0' }}>
                <h2 style={{ fontSize: '1.8em', fontWeight: 500, color: '#fff', marginBottom: '1em' }}>About</h2>
                <p style={{ color: '#aaa', lineHeight: '1.8', fontSize: '1em', marginBottom: '1.2em' }}>
                    
                </p>
                <p style={{ color: '#aaa', lineHeight: '1.8', fontSize: '1em', marginBottom: '1.2em' }}>
                    1. How is the pound-for-pound ranking determined?
                        <br />
                    Ranks ≤ 15 are directly sourced from <a href="https://www.fightmatrix.com/historical-mma-rankings/generated-historical-rankings/?Division=-1" target="_blank" rel="noopener noreferrer" style={{ color: '#98b9dfff', textDecoration: 'none' }}>Fightmatrix's quarterly P4P top 15 lists</a>.
                    Ranks &gt; 15 are estimated from applying customized coefficients to weight class-specific Fightmatrix rankings.
                    Rank N in weight class W is multiplied by a coefficient C(W) to yield an adjusted rank, then adjusted ranks are reranked. C(W) is an estimation of the ratio between the talent pool size W draws from and that of a referential weight class (LW for men, SW for women).<br />
                    - Why should we assume that being ranked N in weight class W is equivalent to being ranked C(W)*N in LW?<br />
                    Because it is not outrageous to assume whatever we define as "skill" or "worth" follows the same distribution across every weight class.<br />
                    - How is C(W) determined?<br />
                    First we estimate the size of the talent pool for each weight class, relative to the referential weight class. We use height as a proxy. We get the mean height MH(W) for each weight class from UFC data. If W_previous and W_next are the previous and next weight classes and A (resp. B) the distance between MH(W) and MH(W_previous) (resp. MH(W_next)), we can assume that the interval [MH(W) - A/2, MH(W) + B/2] is where it is more likely to belong to class W than any other class. Then, we get the proportion of that interval in the general population. It is not clear what counts as general population to be representative in a pro MMA context. Height distributions vary across regions and all regions are not equally professionalized. I decided on a mean height of 176 cm with a standard deviation of 7.5 cm for men, 161 cm/6.5 cm for women.<br />
                    - But many fighters are not fighting at the weight class predicted by their height <br />
                    Yes, but we assume that across a large enough sample, these effects average out. That is, that there are about as many lanky fighters who fight in W instead of W_next as there are stocky ones who fight in W instead of W_previous. Height is a strong correlate to the amount of lean tissue a natural athlete can hold on their frame at a given body fat percentage, therefore of weight class, since (except for HW) it is in every fighter's interest to stay in a similar body fat percentage range (~ 8 to 13% for men).<br />
                    - But some lighter weight classes were professionalized much later than heavier ones, so estimating their talent pool like this can only work up to a certain time in the past.<br />
                    Yes, in fact the height-based coefficient C(W) is only applied after a specific date D(W). Before that, we apply a C(W,I) from interpolating between when I is the first issue where W appears in Fightmatrix rankings and when it reaches D(W).
                    - 

                </p>

                <p style={{ color: '#aaa', lineHeight: '1.8', fontSize: '1em' }}>
                    2. The P4P rankings are obviously meant as a rough estimation, good enough for large-scale comparisons. On an individual level each rank may not exactly match "common sense" and should be considered with a decent margin of error around it. This is already true of any weight class-specific ranking but even more in this case.
                </p>

                <p style={{ color: '#aaa', lineHeight: '1.8', fontSize: '1em' }}>
                    3. North Caucasus republics (spearheaded by Dagestan) have taken over the MMA world in the last 10 years with their abnormal density of high-level fighters. Because they are counted as Russian in every data source, it was still frustratingly unclear just how abnormal they were, especially when compared to their independent South Caucasian neighbors (Georgia, Armenia, Azerbaijan) who also perform well. Sherdog and Tapology sometimes specify local data, but it is often missing, and fighters are often reported as from the city they currently train in. I scrape locality from Sherdog and, in the absence of explicit mention of North Caucasus location, manually research each case.
                    One could think of using names (only among fighters listed as Russian on FM) since North Caucasian names are so distinctive and since we could consider that second generation North Caucasians (emigration to Moscow for instance) carrying those distinctive names are still rare enough outside of North Caucasus. However there is a little too much overlap with Azerbaijan, Tajikistan or even other Russian regions further up North with endogenous Muslim presence, especially bordering Kazakhstan.
                </p>
                <p style={{ color: '#aaa', lineHeight: '1.8', fontSize: '1em' }}>
                    4. It is important to be able to widely vary N when comparing countries based on how many fighters they have in the top N. A larger N is obviously useful for statistical significance. A smaller N is useful to gauge the ability to produce elite-level, not just pro-level talent. It is also useful because countries hosting big local organisations tend to show skewed numbers because their fighters can just fight each other and inflate ELOs for upper subsets.
                </p>
            </div>
        </div>
    );
}

export default CountryRankings;
